(() => {
  const FACET_ORDER = [
    "availability",
    "price",
    "wine_type",
    "producer",
    "region",
    "originCountry",
    "denomination",
    "grape",
    "format",
    "vintage",
    "alcohol_by_volume",
  ];
  const WINE_FACET_ORDER = [
    "wine_type",
    "denomination",
    "producer",
    "price",
    "availability",
    "region",
    "grape",
    "originCountry",
    "format",
    "vintage",
    "alcohol_by_volume",
  ];
  const FACET_INITIAL_VALUES = 6;
  const SCALAR_FACETS = new Set(["producer", "region", "originCountry", "denomination"]);
  // Equivalences come from the API-owned dictionary, never inferred by the theme.
  const canonicalFacetOptions = new Map();
  const facetOption = (param, value) => canonicalFacetOptions.get(`${param}\0${value}`);
  const facetIdentity = (param, value) => facetOption(param, value)?.value || String(value);
  const groupFacetValues = (param, values = [], selected = []) => {
    for (const item of values) {
      if (!item.label || !item.aliases) continue;
      for (const alias of [item.value, ...item.aliases])
        canonicalFacetOptions.set(`${param}\0${alias}`, item);
    }
    const buckets = new Map();
    for (const item of values) {
      if (!item.value) continue;
      buckets.set(item.value, Math.max(buckets.get(item.value) || 0, item.count));
    }
    for (const value of selected) if (!buckets.has(value)) buckets.set(value, 0);
    const groups = new Map();
    for (const [value, count] of buckets) {
      const key = facetIdentity(param, value);
      if (!groups.has(key)) groups.set(key, { key, value: key, values: [], count: 0 });
      const group = groups.get(key);
      group.values.push(value);
      group.count += count;
      const option = facetOption(param, value);
      if (option) {
        group.value = option.value;
        group.aliases = option.aliases;
      }
    }
    return [...groups.values()];
  };
  const facetSearchText = (value) =>
    String(value).normalize("NFD").replace(/\p{M}/gu, "").toLocaleLowerCase().trim();
  const volumeMl = (value) => {
    const match = /^(\d+(?:[.,]\d+)?)\s*(ml|cl|l)$/iu.exec(value.trim());
    return match
      ? Number(match[1].replace(",", ".")) * { ml: 1, cl: 10, l: 1000 }[match[2].toLowerCase()]
      : Infinity;
  };
  const countText = (t, key, count, locale, values = {}) => {
    const resource = t[key];
    const template =
      typeof resource === "string"
        ? resource
        : resource[new Intl.PluralRules(locale || "en").select(count)] || resource.other;
    return formatTemplate(template, { ...values, count: localeNumber(count, locale) });
  };
  const priceContext = (host, data) => {
    const expected = host.dataset.currency;
    const currencies = [
      ...new Set((data.results || []).map((p) => p.price?.currency).filter(Boolean)),
    ];
    const currency = expected || (currencies.length === 1 ? currencies[0] : "");
    const valid = /^[A-Z]{3}$/.test(currency) && currencies.every((code) => code === currency);
    const formatter = valid
      ? new Intl.NumberFormat(host.dataset.locale || "en", {
          style: "currency",
          currency,
        })
      : null;
    const symbol = formatter?.formatToParts(0).find((part) => part.type === "currency")?.value;
    return {
      currency,
      valid,
      actual: currencies.join(" / ") || "—",
      decimals: formatter?.resolvedOptions().maximumFractionDigits ?? 2,
      unit: symbol && symbol !== currency ? `${symbol} ${currency}` : currency,
    };
  };
  const parseRangeNumber = (input, locale, decimals, max = Infinity) => {
    const raw = input.trim();
    if (!raw) return { value: null, valid: true };
    const parts = new Intl.NumberFormat(locale || "en").formatToParts(12345.6);
    const decimal = parts.find((p) => p.type === "decimal")?.value || ".";
    const group = parts.find((p) => p.type === "group")?.value || ",";
    const normalized = raw.replace(/[\u00a0\u202f]/g, " ");
    const grouping = /\s/u.test(group) ? " " : group;
    const pieces = normalized.split(decimal);
    if (
      pieces.length > 2 ||
      (pieces[1] !== undefined && (!/^\d+$/.test(pieces[1]) || pieces[1].length > decimals))
    )
      return { valid: false };
    const integers = pieces[0].split(grouping);
    if (
      !integers.every((part) => /^\d+$/.test(part)) ||
      (integers.length > 1 &&
        (integers[0].length > 3 || integers.slice(1).some((part) => part.length !== 3)))
    )
      return { valid: false };
    const value = Number(`${integers.join("")}${pieces[1] === undefined ? "" : `.${pieces[1]}`}`);
    return { value, valid: Number.isFinite(value) && value >= 0 && value <= max };
  };
  if (
    ((document.documentElement.dataset.boiSearchRuntime = "booting"),
    window.__boiStorefrontSearchLoaded)
  )
    return;
  window.__boiStorefrontSearchLoaded = !0;
  const API_FILTER_PARAMS = [
      "producer",
      "category",
      "region",
      "originCountry",
      "denomination",
      "grape",
      "format",
      "vintage",
      "wineType",
      "availability",
    ],
    RANGE_FILTER_PARAMS = ["priceMin", "priceMax", "alcoholMin", "alcoholMax"],
    ALL_FILTER_PARAMS = [...API_FILTER_PARAMS, ...RANGE_FILTER_PARAMS],
    FACET_PARAMS = {
      producer: "producer",
      region: "region",
      originCountry: "originCountry",
      denomination: "denomination",
      grape: "grape",
      format: "format",
      vintage: "vintage",
      wine_type: "wineType",
      availability: "availability",
    },
    DEFAULT_I18N = {
      loading: "Loading search results…",
      suggestions: "Popular suggestions",
      categories: "Categories",
      collections: "Collections",
      pages: "Pages",
      products: "Products",
      no_results: "No results",
      view_all: "View all results",
      view_all_template: {
        one: "View {count} product",
        other: "View all {count} products",
      },
      filters: "Filters",
      filter_results: "Filter results",
      close: "Close",
      clear_filters: "Clear all",
      apply: "Apply",
      price_min_placeholder: "Min price",
      price_max_placeholder: "Max price",
      alcohol_min_placeholder: "Min alcohol",
      alcohol_max_placeholder: "Max alcohol",
      status_template: {
        one: "{count} result for “{query}”",
        other: "{count} results for “{query}”",
      },
      did_you_mean: "Did you mean",
      sort: "Sort results",
      sort_relevance: "Relevance",
      sort_price_asc: "Price: low to high",
      sort_price_desc: "Price: high to low",
      sort_newest: "Newest",
      facets_aria: "Search filters",
      pagination_aria: "Search pages",
      previous_page: "Previous page",
      next_page: "Next page",
      remove_filter: "Remove {value}",
      in_stock: "In stock",
      backorder: "Available to order",
      out_of_stock: "Out of stock",
      view_product: "View product",
      add_to_cart: "Add to cart",
      quantity: "Quantity",
      sold_out: "Sold out",
      show_more: "Show more",
      show_less: "Show less",
      filter_count_template: {
        one: "Filter ({count})",
        other: "Filters ({count})",
      },
      show_results_template: {
        one: "Show {count} product",
        other: "Show {count} products",
      },
      card_suggested_price: "Recommended price:",
      card_vat_included: "VAT included",
      card_vat_excluded: "VAT excluded",
      card_region: "Region",
      card_country: "Country",
      card_grape: "Grape variety",
      card_spirit_type: "Type",
      card_beer_type: "Beer type",
      card_olive_variety: "Olive variety",
      card_vinegar_material: "Base ingredient",
      card_aging: "Ageing",
      award: "Award-winning product",
      marketplace: "Marketplace product",
      fast_shipping: "Fast shipping",
      sale: "Save {percent}%",
      error: "Search is temporarily unavailable. Native Shopify search remains available.",
      retry: "Try again",
      vat_exclusive: "VAT excl.",
      vat_inclusive: "VAT incl.",
      b2b_unavailable: "B2B pricing is currently unavailable. Use Shopify search.",
      facet_producer: "Producer",
      facet_region: "Region",
      facet_originCountry: "Country",
      facet_denomination: "Denomination",
      facet_grape: "Grape",
      facet_format: "Format",
      facet_vintage: "Vintage",
      facet_wineType: "Wine type",
      facet_availability: "Availability",
      facet_price: "Price",
      facet_alcohol: "Alcohol",
      facet_count_unavailable: "Count unavailable for this option",
      facet_show_all: "Show all",
      search_facet: "Search {facet}",
      facet_no_matches: "No matching options",
      applied_filters: "Applied filters",
      filters_live: "Results update as you select",
      range_invalid: "Enter a minimum no greater than the maximum",
      range_number_invalid: "Enter a valid amount using the storefront number format",
      selection_limit: "Too many options selected. Remove an option first.",
      range_from: "From {value}",
      range_to: "Up to {value}",
      facets_unavailable:
        "Filters are temporarily unavailable. You can still remove applied filters.",
      range_min_label: "Min",
      range_max_label: "Max",
      price_min_aria: "Minimum price ({currency})",
      price_max_aria: "Maximum price ({currency})",
      alcohol_min_aria: "Minimum alcohol (%)",
      alcohol_max_aria: "Maximum alcohol (%)",
      price_currency_unavailable:
        "Price filtering is unavailable: prices are returned in {actual}, while the storefront uses {currency}.",
    },
    CACHE_TTL = 3e4,
    responseCache = new Map(),
    b2bContextPromises = new Map(),
    translations = (host) => {
      let extra = {};
      try {
        host.dataset.i18n && (extra = JSON.parse(host.dataset.i18n));
      } catch {
        extra = {};
      }
      const translated = {
        ...DEFAULT_I18N,
        ...Object.fromEntries(
          Object.entries(extra).filter(
            ([, value]) => typeof value !== "string" || !/translation missing:/i.test(value),
          ),
        ),
      };
      return (
        host.dataset.locale === "it" &&
          ((translated.vat_exclusive = "IVA Escl."), (translated.vat_inclusive = "IVA Incl.")),
        host.dataset.locale === "fr" &&
          ((translated.vat_exclusive = "TVA excl."), (translated.vat_inclusive = "TVA incl.")),
        translated
      );
    },
    formatTemplate = (template, values) =>
      template.replace(/\{(\w+)\}|__boi_(\w+)__/g, (match, legacyKey, liquidKey) => {
        const key = liquidKey || legacyKey;
        return Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match;
      }),
    debounce = (fn, wait) => {
      let timer;
      const debounced = (...args) => {
        (clearTimeout(timer), (timer = setTimeout(() => fn(...args), wait)));
      };
      return ((debounced.cancel = () => clearTimeout(timer)), debounced);
    },
    uuid = () => {
      try {
        return crypto.randomUUID();
      } catch {
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
          const random = Math.floor(Math.random() * 16);
          return (character === "x" ? random : (random & 3) | 8).toString(16);
        });
      }
    },
    analyticsCartToken = (token) => {
      const privateKeySeparator = token.indexOf("?key=");
      return privateKeySeparator > 0 ? token.slice(0, privateKeySeparator) : token;
    };
  let sessionId = (() => {
      const key = "boi-search-session";
      try {
        let value = sessionStorage.getItem(key);
        return (value || ((value = uuid()), sessionStorage.setItem(key, value)), value);
      } catch {
        return uuid();
      }
    })(),
    activeB2BPartition = null;
  const directQuickAddVariants = new Set(),
    ATTRIBUTION_STORAGE_KEY = "boi-search-attribution-v1",
    attributionRecords = () => {
      if (!analyticsAllowed()) return [];
      try {
        const records = JSON.parse(localStorage.getItem(ATTRIBUTION_STORAGE_KEY) || "[]");
        if (!Array.isArray(records)) return [];
        const active = records.filter(
          (record) => record?.expiresAt > Date.now() && record.searchId && record.productId,
        );
        return (
          active.length !== records.length &&
            localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(active)),
          active
        );
      } catch {
        return [];
      }
    },
    saveAttributionRecord = (result, product) => {
      if (
        !analyticsAllowed() ||
        !result?.attributionToken ||
        !result.searchId ||
        !product?.productId
      )
        return;
      const numericProductId = numericShopifyId(product.productId);
      if (numericProductId)
        try {
          const records = attributionRecords().filter(
            (record) => String(record.productNumericId) !== numericProductId,
          );
          (records.push({
            eventId: uuid(),
            searchId: result.searchId,
            attributionToken: result.attributionToken,
            productId: product.productId,
            productNumericId: numericProductId,
            expiresAt: Date.now() + 7 * 864e5,
          }),
            localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(records.slice(-20))));
        } catch {}
    },
    numberFormatters = new Map(),
    numberFormatter = (locale, options) => {
      const key = JSON.stringify([locale, options]);
      if (!numberFormatters.has(key)) {
        if (numberFormatters.size >= 40)
          numberFormatters.delete(numberFormatters.keys().next().value);
        numberFormatters.set(key, new Intl.NumberFormat(locale, options));
      }
      return numberFormatters.get(key);
    },
    money = (value, currency, locale) => {
      try {
        return numberFormatter(locale, { style: "currency", currency }).format(value);
      } catch {
        return `${Number(value).toFixed(2)} ${currency}`;
      }
    },
    rootPath = (root) => {
      const normalized = root || "/";
      return normalized.endsWith("/") ? normalized : `${normalized}/`;
    },
    productUrl = (root, handle) =>
      `${rootPath(root)}products/${encodeURIComponent(handle)}`.replace(/\/+/g, "/"),
    searchUrl = (root, query) =>
      `${`${rootPath(root)}search`.replace(/\/+/g, "/")}?q=${encodeURIComponent(query)}`,
    safePath = (path) =>
      typeof path == "string" && path.startsWith("/") && !path.startsWith("//") ? path : null,
    localizedNavigationPath = (item, root) => {
      const localized = safePath(item.localizedPath);
      if (localized) return localized;
      const path = safePath(item.path);
      if (!path) return rootPath(root);
      const prefix = rootPath(root).replace(/\/$/, "");
      return !prefix || path === prefix || path.startsWith(`${prefix}/`)
        ? path
        : `${prefix}${path}`;
    },
    element = (tag, attrs = {}, text) => {
      const node = document.createElement(tag);
      for (const [key, value] of Object.entries(attrs))
        value == null ||
          value === !1 ||
          (key === "class"
            ? (node.className = value)
            : key === "dataset"
              ? Object.assign(node.dataset, value)
              : key === "htmlFor"
                ? (node.htmlFor = value)
                : key === "selected"
                  ? (node.selected = !!value)
                  : key === "checked"
                    ? (node.checked = !!value)
                    : key.startsWith("aria-") || key === "role"
                      ? node.setAttribute(key, String(value))
                      : (node[key] = value));
      return (text !== void 0 && (node.textContent = text), node);
    },
    cacheKey = (base, path) => `${base.replace(/\/$/, "")}${path}`,
    safePartition = (value) => {
      let hash = 2166136261;
      for (const character of String(value))
        ((hash ^= character.charCodeAt(0)), (hash = Math.imul(hash, 16777619)));
      return (hash >>> 0).toString(36);
    },
    invalidateB2BContext = (partition, entry) => {
      if (b2bContextPromises.get(partition) === entry) {
        b2bContextPromises.delete(partition);
        try {
          sessionStorage.removeItem(`boi-b2b-context:${partition}`);
        } catch {}
      }
    },
    B2B_STOREFRONT_BUILD = "b2b-client-event-20261007-1",
    reportB2BFailure = (host, stage, error, reason = "client_exception", responseCountry) => {
      if (!(host?.dataset.b2b !== "true" || error?.name === "AbortError"))
        try {
          const prior = error?.boiB2BDiagnostic,
            country = (value) =>
              typeof value == "string" && /^[A-Z]{2}$/.test(value) ? value : null,
            stages = [
              "context_bootstrap",
              "context_response_validation",
              "search_dispatch",
              "response_parse",
              "render",
            ],
            reasons = [
              "client_exception",
              "bootstrap_http_error",
              "issued_country_mismatch",
              "context_token_missing",
              "json_parse_failed",
              "fetch_failed",
              "search_http_error",
              "country_claim_mismatch",
            ],
            names = [
              "Error",
              "TypeError",
              "SyntaxError",
              "RangeError",
              "ReferenceError",
              "URIError",
              "SecurityError",
              "QuotaExceededError",
              "NetworkError",
            ],
            diagnostic = {
              stage: stages.includes(prior?.stage || stage)
                ? prior?.stage || stage
                : "search_dispatch",
              requestedCountry: country(prior?.requestedCountry || host.dataset.country),
              responseCountry: country(
                prior
                  ? prior.responseCountry
                  : responseCountry === void 0
                    ? host.boiB2BResponseCountry
                    : responseCountry,
              ),
              storefrontBuild: B2B_STOREFRONT_BUILD,
              exceptionName: names.includes(error?.name) ? error.name : "Error",
              reason: reasons.includes(prior?.reason || reason)
                ? prior?.reason || reason
                : "client_exception",
            };
          if (((host.boiB2BDiagnostic = diagnostic), !prior)) {
            if (error && typeof error == "object")
              try {
                error.boiB2BDiagnostic = diagnostic;
              } catch {}
            console.warn("Bottle B2B failure", diagnostic);
          }
        } catch {}
    },
    emitB2BFailure = (host) => {
      if (!(host?.dataset.b2b !== "true" || host.boiB2BFailureSentSequence === host.sequence))
        try {
          const data = host.boiB2BDiagnostic;
          if (!data || !host.dataset.apiBase) return;
          const country = (value) =>
              typeof value == "string" && /^[A-Z]{2}$/.test(value) ? value : null,
            stages = [
              "context_bootstrap",
              "context_response_validation",
              "search_dispatch",
              "response_parse",
              "render",
            ],
            names = [
              "Error",
              "TypeError",
              "SyntaxError",
              "RangeError",
              "ReferenceError",
              "URIError",
              "SecurityError",
              "QuotaExceededError",
              "NetworkError",
            ],
            reasons = [
              "client_exception",
              "bootstrap_http_error",
              "issued_country_mismatch",
              "context_token_missing",
              "json_parse_failed",
              "fetch_failed",
              "search_http_error",
              "country_claim_mismatch",
            ],
            event = {
              stage: stages.includes(data.stage) ? data.stage : "render",
              requestedCountry: country(data.requestedCountry),
              responseCountry: country(data.responseCountry),
              storefrontBuild: B2B_STOREFRONT_BUILD,
              exceptionName: names.includes(data.exceptionName) ? data.exceptionName : "Error",
              reason: reasons.includes(data.reason) ? data.reason : "client_exception",
              freshBootstrap: host.boiB2BFreshBootstrap === !0,
            };
          ((host.boiB2BFailureSentSequence = host.sequence),
            fetch(`${host.dataset.apiBase.replace(/\/$/, "")}/v1/learning/events`, {
              method: "POST",
              credentials: "omit",
              referrerPolicy: "no-referrer",
              keepalive: !0,
              headers: { "content-type": "application/json" },
              body: JSON.stringify(event),
              signal:
                typeof AbortSignal < "u" && typeof AbortSignal.timeout == "function"
                  ? AbortSignal.timeout(1500)
                  : void 0,
            }).catch(() => {}));
        } catch {}
    },
    contextFor = async (host) => {
      if (host?.dataset.b2b !== "true") return { audience: "retail", token: "" };
      const locationId = host.boiCompanyLocationId || host.dataset.companyLocationId;
      if (
        ((host.boiCompanyLocationId = locationId),
        host.removeAttribute("data-company-location-id"),
        !locationId || !host.dataset.contextUrl)
      )
        throw new Error("B2B context unavailable");
      const partition = safePartition(locationId),
        country = host.dataset.country;
      try {
        const storedPartition = sessionStorage.getItem("boi-b2b-location-partition");
        (((storedPartition && storedPartition !== partition) ||
          (activeB2BPartition && activeB2BPartition !== partition)) &&
          ((sessionId = uuid()),
          sessionStorage.setItem("boi-search-session", sessionId),
          responseCache.clear(),
          b2bContextPromises.clear()),
          sessionStorage.setItem("boi-b2b-location-partition", partition));
      } catch {
        activeB2BPartition &&
          activeB2BPartition !== partition &&
          ((sessionId = uuid()), responseCache.clear(), b2bContextPromises.clear());
      }
      activeB2BPartition = partition;
      let entry = b2bContextPromises.get(partition);
      if (
        (entry &&
          (entry.country !== country || (entry.context && entry.context.country !== country)) &&
          (invalidateB2BContext(partition, entry), (entry = void 0)),
        !entry)
      ) {
        ((entry = { country, promise: null, context: null }),
          b2bContextPromises.set(partition, entry));
        const current = entry;
        entry.promise = (async () => {
          const storageKey = `boi-b2b-context:${partition}`;
          try {
            const cached = JSON.parse(sessionStorage.getItem(storageKey) || "null");
            if (cached?.country !== country) sessionStorage.removeItem(storageKey);
            else if (cached?.token && cached.expiresAt > Date.now() + 3e4)
              return { audience: "b2b", ...cached };
          } catch {
            sessionStorage.removeItem(storageKey);
          }
          let diagnosticStage = "context_bootstrap",
            diagnosticReason = "fetch_failed",
            responseCountry = null;
          try {
            ((current.bootstrapPending = !0), (host.boiB2BFreshBootstrap = !0));
            const response = await fetch(host.dataset.contextUrl, {
              method: "POST",
              credentials: "same-origin",
              headers: {
                "content-type": "application/json",
                "x-search-session": sessionId,
                ...consentHeaders(),
              },
              body: JSON.stringify({ companyLocationId: locationId }),
            });
            if (((diagnosticReason = "bootstrap_http_error"), !response.ok))
              throw new Error("B2B context unavailable");
            ((diagnosticStage = "response_parse"), (diagnosticReason = "json_parse_failed"));
            const value = await response.json();
            if (
              ((responseCountry = value?.country ?? null),
              (diagnosticStage = "context_response_validation"),
              (diagnosticReason = value?.token
                ? "issued_country_mismatch"
                : "context_token_missing"),
              b2bContextPromises.get(partition) !== current || host.dataset.country !== country)
            )
              throw new DOMException("B2B context changed", "AbortError");
            if (!value?.token || value.country !== country)
              throw new Error("B2B context unavailable");
            diagnosticReason = "client_exception";
            try {
              sessionStorage.setItem(
                storageKey,
                JSON.stringify({
                  token: value.token,
                  catalogId: value.catalogId,
                  country: value.country,
                  companyLocationId: value.companyLocationId,
                  expiresAt: Date.now() + value.expiresIn * 1e3,
                }),
              );
            } catch {}
            return {
              audience: "b2b",
              token: value.token,
              catalogId: value.catalogId,
              country: value.country,
              companyLocationId: value.companyLocationId,
              expiresAt: Date.now() + value.expiresIn * 1e3,
            };
          } catch (error) {
            throw (
              b2bContextPromises.get(partition) === current &&
                host.dataset.country === country &&
                reportB2BFailure(host, diagnosticStage, error, diagnosticReason, responseCountry),
              error
            );
          } finally {
            current.bootstrapPending = !1;
          }
        })();
      }
      host.boiB2BFreshBootstrap ||= entry.bootstrapPending === !0;
      let context;
      try {
        context = await entry.promise;
      } catch (error) {
        throw (invalidateB2BContext(partition, entry), error);
      }
      if (b2bContextPromises.get(partition) !== entry || host.dataset.country !== country)
        throw new DOMException("B2B context changed", "AbortError");
      if (context.country !== country)
        throw (invalidateB2BContext(partition, entry), new Error("B2B context unavailable"));
      return (
        (entry.context = context),
        context.expiresAt <= Date.now() + 3e4
          ? (invalidateB2BContext(partition, entry), contextFor(host))
          : context
      );
    };
  let learningModule, consentModule;
  const consentHeaders = () => consentModule?.analyticsHeaders() ?? {},
    analyticsAllowed = () => consentModule?.analyticsAllowed() === !0;
  document.addEventListener("bottleConsentInvalidated", () => responseCache.clear());
  const producerScript =
      document.currentScript ||
      [...document.scripts].find((script) => script.src.includes("/bottle-search.js")),
    learningAsset = producerScript?.src,
    learningModuleAsset = producerScript?.dataset.learningAsset,
    learning = (host, context) => {
      if (!learningAsset) return Promise.resolve();
      const learningUrl = new URL(learningModuleAsset || "bottle-learning.js", learningAsset),
        producerRevision = new URL(learningAsset).searchParams.get("boi_rev");
      return (
        producerRevision && learningUrl.searchParams.set("boi_rev", producerRevision),
        (learningModule ??= import(learningUrl.href).catch(() => null)),
        learningModule
          .then((module) => ((consentModule = module), module?.bootstrap(host, context, sessionId)))
          .catch(() => {})
      );
    },
    request = async (host, path, signal, retryCountryMismatch = !0) => {
      const diagnosticSequence = host.sequence,
        country = host.dataset.country;
      let diagnosticStage = "context_bootstrap",
        diagnosticReason = "client_exception";
      try {
        const context = await contextFor(host);
        ((host.boiB2BResponseCountry = context.country), (diagnosticStage = "search_dispatch"));
        const partition = safePartition(
            host.boiCompanyLocationId || host.dataset.companyLocationId,
          ),
          entry = b2bContextPromises.get(partition),
          consentReady = learning(host, context);
        if ((path.startsWith("/v1/search?") && (await consentReady), signal?.aborted))
          throw new DOMException("Aborted", "AbortError");
        if (context.audience === "b2b" && host.dataset.country !== country)
          throw new DOMException("B2B context changed", "AbortError");
        const base = host.dataset.apiBase,
          key = `${cacheKey(base, path)}|${context.audience}:${safePartition(context.token)}`,
          cached = responseCache.get(key);
        if (cached && cached.expiresAt > Date.now() && host.nextInteractionKind !== "RETRY")
          return cached.value;
        diagnosticReason = "fetch_failed";
        const response = await fetch(cacheKey(base, path), {
          signal,
          credentials: "omit",
          headers: {
            "x-search-session": sessionId,
            ...consentHeaders(),
            "x-bottle-search-audience": context.audience,
            ...(context.token ? { "x-bottle-search-context": context.token } : {}),
          },
        });
        if (((diagnosticReason = "search_http_error"), !response.ok)) {
          if (response.status === 403 && context.audience === "b2b") {
            let reason;
            try {
              reason = (await response.json())?.error?.reason;
            } catch {}
            if (reason === "country_claim_mismatch") {
              if (
                ((diagnosticReason = "country_claim_mismatch"),
                entry?.context === context && invalidateB2BContext(partition, entry),
                signal?.aborted)
              )
                throw new DOMException("Aborted", "AbortError");
              if (host.dataset.country !== country)
                throw new DOMException("B2B context changed", "AbortError");
              if (retryCountryMismatch) return request(host, path, signal, !1);
            }
          }
          throw new Error(`Search ${response.status}`);
        }
        ((diagnosticStage = "response_parse"), (diagnosticReason = "json_parse_failed"));
        const value = await response.json();
        return (
          (diagnosticReason = "client_exception"),
          path.startsWith("/v1/search?") &&
            learningModule &&
            learningModule
              .then((module) =>
                module?.observeSearch(
                  value,
                  new URLSearchParams(path.split("?")[1]).get("q") || "",
                ),
              )
              .catch(() => {}),
          value.facetStatus !== "unavailable"
            ? responseCache.set(key, { value, expiresAt: Date.now() + CACHE_TTL })
            : responseCache.delete(key),
          responseCache.size > 40 && responseCache.delete(responseCache.keys().next().value),
          value
        );
      } catch (error) {
        throw (
          host.sequence === diagnosticSequence &&
            host.dataset.country === country &&
            reportB2BFailure(
              host,
              diagnosticStage,
              error,
              diagnosticReason,
              diagnosticStage === "context_bootstrap" ? null : void 0,
            ),
          error
        );
      }
    },
    EU_VAT_COUNTRIES = new Set([
      "AT",
      "BE",
      "BG",
      "HR",
      "CY",
      "CZ",
      "DE",
      "DK",
      "EE",
      "ES",
      "FI",
      "FR",
      "GR",
      "HU",
      "IE",
      "IT",
      "LT",
      "LU",
      "LV",
      "MT",
      "NL",
      "PL",
      "PT",
      "RO",
      "SE",
      "SI",
      "SK",
    ]),
    priceDisplayLines = (host, data, product) => {
      const audience = data.pricingContext?.audience === "b2b" ? "B2B" : "RETAIL",
        country = data.pricingContext?.countryCode || host.dataset.country,
        taxesIncluded = host.dataset.taxesIncluded === "true",
        canonical = Number(product.price.min);
      if (!EU_VAT_COUNTRIES.has(country)) return [{ amount: canonical, label: "" }];
      if (audience === "B2B" && country === "IT" && taxesIncluded) {
        const canonicalCents = Math.round(canonical * 100);
        return [
          { amount: Math.round((canonicalCents * 100) / 122) / 100, label: host.t.vat_exclusive },
          { amount: canonical, label: host.t.vat_inclusive },
        ];
      }
      return [
        {
          amount: canonical,
          label: audience === "RETAIL" ? host.t.vat_inclusive : host.t.vat_exclusive,
        },
      ];
    },
    sendProductClick = (api, result, product, position, surface) => {
      if (!analyticsAllowed() || !result?.analyticsToken || !result.searchId || !product?.productId)
        return;
      saveAttributionRecord(result, product);
      const eventId = uuid();
      learningModule &&
        learningModule
          .then((module) => module?.observeClick(result.searchId, product.productId, eventId))
          .catch(() => {});
      const body = JSON.stringify({
          eventId,
          searchId: result.searchId,
          analyticsToken: result.analyticsToken,
          productId: product.productId,
          position,
          surface,
        }),
        endpoint = `${api.replace(/\/$/, "")}/v1/analytics/click`;
      fetch(endpoint, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-search-session": sessionId,
          ...consentHeaders(),
        },
        body,
        keepalive: !0,
        credentials: "omit",
      }).catch(() => {});
    },
    sendProductPageCart = async (endpoint, body) => {
      const waits = [0];
      for (const wait of waits) {
        if (
          (wait && (await new Promise((resolve) => setTimeout(resolve, wait))), !analyticsAllowed())
        )
          return !1;
        try {
          const response = await fetch(endpoint, {
            method: "POST",
            credentials: "omit",
            keepalive: !0,
            headers: {
              "content-type": "application/json",
              "x-search-session": sessionId,
              ...consentHeaders(),
            },
            body,
          });
          if (response.ok) return !0;
          if (
            response.status !== 400 ||
            (await response.json().catch(() => null))?.error?.code !== "ATTRIBUTION_CLICK_NOT_FOUND"
          )
            return !1;
        } catch {
          return !1;
        }
      }
      return !1;
    },
    inventoryLabel = (value, t) => t[value] || value,
    localeNumber = (value, locale, maximumFractionDigits = 1) =>
      numberFormatter(locale, { maximumFractionDigits }).format(value),
    formatVolume = (value, locale) => {
      const match = /^\s*(\d+(?:[.,]\d+)?)\s*ml\s*$/iu.exec(String(value));
      return match
        ? `${localeNumber(Number(match[1].replace(",", ".")) / 10, locale)} cl`
        : String(value);
    },
    titleCaseTechnicalValue = (value, locale) => {
      const text = String(value).trim();
      return !text || text !== text.toLocaleUpperCase(locale) || !/\p{L}/u.test(text)
        ? text
        : `${text.charAt(0).toLocaleUpperCase(locale)}${text.slice(1).toLocaleLowerCase(locale)}`;
    },
    facetValueLabel = (param, value, t, locale) =>
      facetOption(param, value)?.label ||
      (/^(?:facet:|grape:)/u.test(value)
        ? t.selected_filter || "Filtro selezionato"
        : param === "format"
          ? formatVolume(value, locale)
          : param === "availability"
            ? inventoryLabel(value, t)
            : ["originCountry", "wineType", "region"].includes(param)
              ? titleCaseTechnicalValue(value, locale)
              : String(value)),
    numericShopifyId = (value) => /(?:^|\/)(\d+)$/.exec(String(value || ""))?.[1] || "",
    discountPercent = (product) => {
      const compare = product.price?.compareAtMin,
        price = product.price?.min;
      return compare > price && compare > 0 ? Math.round(((compare - price) / compare) * 100) : 0;
    },
    promotionFor = (host, product, b2b) => {
      if (!host.promoRules)
        try {
          host.promoRules = JSON.parse(host.dataset.promoConfig || "[]");
        } catch {
          host.promoRules = [];
        }
      const tags = new Set(product.tags || []);
      return (
        host.promoRules
          .filter((promotion) => {
            if (!promotion?.tag || !tags.has(promotion.tag)) return !1;
            const visibility = promotion.visibility || "all";
            return (
              visibility === "all" ||
              (b2b && visibility === "b2b_only") ||
              (!b2b && visibility === "non_b2b_only")
            );
          })
          .sort((left, right) => Number(left.priority) - Number(right.priority))[0] || null
      );
    },
    promotionPrice = (canonical, promotion, b2b) => {
      if (!promotion || promotion.type === "case_quantity_discount") return canonical;
      if (promotion.type === "fixed_amount") {
        const retailFixed = Number(promotion.fixedAmount || 0),
          b2bFixed = Number(promotion.fixedAmountB2b || 0),
          fixed = b2b
            ? b2bFixed > 0
              ? b2bFixed
              : Math.round((retailFixed / 1.22) * 100) / 100
            : retailFixed;
        return Math.max(0, Math.round((canonical - fixed) * 100) / 100);
      }
      return Math.max(
        0,
        Math.floor((Math.round(canonical * 100) * (100 - Number(promotion.percent || 0))) / 100) /
          100,
      );
    },
    facetLabel = (facet, t) => {
      if (facet.key === "price") return t.facet_price;
      if (facet.key === "alcohol_by_volume") return t.facet_alcohol;
      const param = FACET_PARAMS[facet.key];
      return (param && t[`facet_${param}`]) || facet.label;
    };
  const apiLocale = (host) => {
    const language = (host.dataset.locale || "en").toLowerCase().split("-")[0];
    return ["it", "en", "de", "fr"].includes(language) ? language : "en";
  };
  const metaIcons = {
    pin: '<path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="2.5"/>',
    grape:
      '<g transform="translate(2 .5)"><path d="M12 6V4c0-2 2-3 4-3M12 6C8 6 6 4 6 2c4 0 6 1 6 4Z"/><circle cx="8" cy="9" r="3"/><circle cx="15" cy="9" r="3"/><circle cx="5" cy="14" r="3"/><circle cx="12" cy="14" r="3"/><circle cx="9" cy="19" r="3"/></g>',
    bottle:
      '<g transform="translate(0 .5)"><path d="M9 2h6v4l2 4v11H7V10l2-4V2Zm0 3h6M7 12h10M7 18h10"/></g>',
    olive:
      '<g transform="translate(-1.5 .5)"><path d="M5 21 18 3M12 11C7 11 5 8 5 4c5 0 8 2 8 6M15 8c0-4 3-6 7-6 0 4-2 7-6 7"/><ellipse cx="9" cy="16" rx="3" ry="4" transform="rotate(35 9 16)"/></g>',
    ingredient:
      '<path d="M12 22V12M12 16C5 16 2 12 2 6c7 0 10 3 10 10Zm0-4C12 5 16 2 22 2c0 7-3 10-10 10Z"/>',
    aging: '<circle cx="12" cy="12" r="9"/><path d="M12 6v6l4 2"/>',
    hop: '<g transform="translate(0 1)"><path d="M12 3C7 3 5 6 5 10c0 6 7 11 7 11s7-5 7-11c0-4-2-7-7-7ZM12 3V1M12 3v18M6 7l6 4 6-4M5 11l7 5 7-5M8 17l4 2 4-2"/></g>',
    vat: '<circle cx="12" cy="12" r="9"/><path d="m8 12 2.5 2.5 5.5-5.5"/>',
  };
  const cardIcons = new Map();
  const runtimeAwardIcon = new URL(
    "bottle-search-premiato.svg",
    document.currentScript?.src || location.href,
  ).href;
  const cardIcon = (name) => {
    if (cardIcons.has(name)) return cardIcons.get(name).cloneNode(true);
    const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    icon.setAttribute("viewBox", "0 0 24 24");
    icon.setAttribute("aria-hidden", "true");
    icon.setAttribute("focusable", "false");
    icon.innerHTML = metaIcons[name];
    cardIcons.set(name, icon);
    return icon.cloneNode(true);
  };
  const metadataText = (value) =>
    typeof value === "string"
      ? value.trim()
      : Array.isArray(value)
        ? value.filter((item) => typeof item === "string" && item.trim()).join(", ")
        : "";
  const cardMetadata = (host, product) => {
    const type = metadataText(product.productType).toLowerCase();
    const tags = new Set(product.tags || []);
    const wine =
      ["vino rosso", "vino bianco", "vino rosato", "bollicine"].includes(type) || tags.has("vini");
    const oil = type === "olio" || (!type && tags.has("olio")),
      vinegar = type === "aceto" || (!type && tags.has("aceto"));
    const spirit = type === "spirits" || (!type && tags.has("spirits")),
      beer = type === "birra" || (!type && (tags.has("birra") || tags.has("birre")));
    const metadata = element("span", { class: "boi-search-meta" });
    const first = element("span", { class: "boi-search-meta__line" });
    const item = (container, value, icon, label, modifier = "") => {
      if (!value) return;
      const node = element("span", { class: `boi-search-meta__item ${modifier}` });
      if (!icon && !label) {
        node.textContent = value;
        container.append(node);
        return;
      }
      if (icon) node.append(cardIcon(icon));
      const text = element("span");
      if (label) text.append(element("span", { class: "boi-search-meta__label" }, `${label}: `));
      text.append(document.createTextNode(value));
      node.append(text);
      container.append(node);
    };
    const origin =
      wine || oil
        ? metadataText(product.region)
        : spirit || beer
          ? metadataText(product.originCountry)
          : "";
    item(first, origin, "pin", wine || oil ? host.t.card_region : host.t.card_country);
    item(first, metadataText(product.vintage), null, null, "boi-search-meta__vintage");
    item(
      first,
      metadataText(product.format) ? formatVolume(product.format, host.dataset.locale) : "",
    );
    if (
      !oil &&
      !vinegar &&
      typeof product.alcoholByVolume === "number" &&
      Number.isFinite(product.alcoholByVolume)
    )
      item(first, `${localeNumber(product.alcoholByVolume, host.dataset.locale)}%`);
    if (first.childNodes.length) metadata.append(first);
    const extra = product.cardMetadata || {};
    const detail = (value, icon, label) => {
      if (!metadataText(value)) return;
      const row = element("span", { class: "boi-search-meta__detail" });
      item(row, metadataText(value), icon, label);
      metadata.append(row);
    };
    if (wine) detail(product.grape, "grape", host.t.card_grape);
    if (spirit) detail(extra.spiritType, "bottle", host.t.card_spirit_type);
    if (oil) detail(extra.oliveVariety, "olive", host.t.card_olive_variety);
    if (vinegar) detail(extra.vinegarMaterial, "ingredient", host.t.card_vinegar_material);
    if (beer) detail(extra.beerType, "hop", host.t.card_beer_type);
    if (spirit || vinegar) detail(extra.aging, "aging", host.t.card_aging);
    return metadata.childNodes.length ? metadata : null;
  };
  const cardVatLabel = (host, data) => {
    const b2b = data.pricingContext?.audience === "b2b";
    const country = b2b
      ? data.pricingContext.countryCode
      : data.pricingContext?.countryCode || host.dataset.country;
    if (host.dataset.b2b === "true" && !b2b) return "";
    if (
      b2b &&
      (host.dataset.b2b !== "true" ||
        !host.boiCompanyLocationId ||
        country !== host.boiB2BResponseCountry)
    )
      return "";
    if (!EU_VAT_COUNTRIES.has(country)) return "";
    if (b2b && country === "IT" && host.dataset.taxesIncluded !== "true") return "";
    return b2b && country !== "IT" ? host.t.card_vat_excluded : host.t.card_vat_included;
  };
  const premiumPrice = (host, data, product, className) => {
    const price = element("span", { class: `${className} boi-search-price` });
    const b2b = data.pricingContext?.audience === "b2b";
    const promotion = promotionFor(host, product, b2b);
    const canonical = Number(product.price.min);
    const amount = promotionPrice(canonical, promotion, b2b);
    const extra = promotion && promotion.type !== "case_quantity_discount" && amount < canonical;
    if (promotion?.banner) {
      const banner = element(
        "span",
        { class: "boi-search-price__banner" },
        String(promotion.banner),
      );
      if (promotion.background) banner.style.backgroundColor = String(promotion.background);
      if (promotion.color) banner.style.color = String(promotion.color);
      price.append(banner);
    }
    const display = money(amount, product.price.currency, host.dataset.locale);
    const main = element("span", { class: "boi-search-price__main" });
    main.append(
      element(
        "span",
        {
          class: `boi-search-price__current${extra ? " is-extra" : ""}${display.length > 11 ? " is-long" : ""}`,
        },
        display,
      ),
    );
    const reference = product.price.compareAtMin;
    const validReference =
      !b2b &&
      (!host.dataset.currency || host.dataset.currency === product.price.currency) &&
      product.price.min === product.price.max &&
      numericShopifyId(product.variantId) !== "" &&
      numericShopifyId(product.variantId) === numericShopifyId(product.selectedVariant?.id) &&
      typeof reference === "number" &&
      Number.isFinite(reference) &&
      reference > canonical &&
      canonical >= 0;
    if (validReference)
      main.append(
        element("span", { class: "boi-search-price__discount" }, `−${discountPercent(product)}%`),
      );
    price.append(main);
    if (validReference) {
      const row = element("span", { class: "boi-search-price__reference" });
      row.append(
        document.createTextNode(host.t.card_suggested_price),
        element("del", {}, money(reference, product.price.currency, host.dataset.locale)),
      );
      price.append(row);
    }
    const label = cardVatLabel(host, data);
    if (label) {
      const vat = element("span", { class: "boi-search-price__vat" });
      vat.append(cardIcon("vat"), document.createTextNode(label));
      price.append(vat);
    }
    if (b2b) {
      const priceProduct = { ...product, price: { ...product.price, min: amount } };
      const lines = priceDisplayLines(host, data, priceProduct);
      for (const line of lines) {
        if (line.amount === amount && (lines.length > 1 || !line.label)) continue;
        const row = element("span", { class: "boi-search-price__context" });
        if (line.amount !== amount)
          row.append(
            document.createTextNode(
              money(line.amount, product.price.currency, host.dataset.locale),
            ),
          );
        if (line.label) row.append(element("small", {}, line.label));
        if (line.amount !== amount || !label) price.append(row);
      }
    }
    return price;
  };
  const badgeContent = new WeakMap();
  const tooltipCopyCache = new WeakMap();
  const tooltipCopy = (host, key) => {
    if (!tooltipCopyCache.has(host)) tooltipCopyCache.set(host, new Map());
    const cache = tooltipCopyCache.get(host);
    if (cache.has(key)) return cache.get(key);
    const template = document.createElement("template");
    template.innerHTML = typeof host.t[key] === "string" ? host.t[key] : "";
    template.content.querySelectorAll("script,style").forEach((node) => node.remove());
    template.content.querySelectorAll("br").forEach((node) => node.replaceWith("\u0000"));
    const text = template.content.textContent.replace(/\s+/g, " ").trim();
    const [title, ...body] = text.split("\u0000");
    const copy = { title: title.trim(), body: body.join("\n").trim() };
    cache.set(key, copy);
    return copy;
  };
  const searchTooltips = (() => {
    const hosts = new Set();
    let controller, panel, active, owner, observer, closeTimer, frame, pinned;
    const inside = (target) =>
      target instanceof Node && (active?.contains(target) || panel?.contains(target));
    const close = () => {
      clearTimeout(closeTimer);
      if (frame) cancelAnimationFrame(frame);
      frame = null;
      observer?.disconnect();
      if (active) {
        active.setAttribute("aria-expanded", "false");
        active.removeAttribute("aria-describedby");
      }
      if (panel) panel.hidden = true;
      active = owner = null;
      pinned = false;
    };
    const position = () => {
      frame = null;
      if (!active?.isConnected) return close();
      const rect = active.getBoundingClientRect();
      const viewport = window.visualViewport;
      const minX = (viewport?.offsetLeft || 0) + 12,
        minY = (viewport?.offsetTop || 0) + 12;
      const width = viewport?.width || innerWidth,
        height = viewport?.height || innerHeight;
      panel.style.maxWidth = `${Math.max(0, width - 24)}px`;
      panel.style.maxHeight = `${Math.max(0, height - 24)}px`;
      const box = panel.getBoundingClientRect();
      const left = Math.max(
        minX,
        Math.min(rect.left + rect.width / 2 - box.width / 2, minX + width - 24 - box.width),
      );
      const above =
        rect.bottom + 9 + box.height > minY + height - 12 && rect.top > minY + box.height + 9;
      const top = Math.max(
        minY,
        Math.min(
          above ? rect.top - box.height - 9 : rect.bottom + 9,
          minY + height - 24 - box.height,
        ),
      );
      panel.style.left = `${left}px`;
      panel.style.top = `${top}px`;
      panel.style.setProperty(
        "--boi-tooltip-arrow",
        `${Math.max(12, Math.min(rect.left + rect.width / 2 - left, box.width - 24))}px`,
      );
      panel.classList.toggle("is-above", above);
    };
    const queuePosition = () => {
      if (active && !frame) frame = requestAnimationFrame(position);
    };
    const open = (trigger) => {
      clearTimeout(closeTimer);
      if (trigger === active) return;
      const content = badgeContent.get(trigger);
      if (!content) return;
      close();
      if (!panel) {
        panel = element("div", {
          id: `boi-search-tooltip-${uuid()}`,
          class: "boi-search-tooltip",
          role: "tooltip",
          hidden: true,
        });
        document.body.append(panel);
      }
      panel.replaceChildren(
        element("strong", { class: "boi-search-tooltip__title" }, content.title),
      );
      if (content.body)
        panel.append(element("div", { class: "boi-search-tooltip__body" }, content.body));
      active = trigger;
      owner = content.host;
      trigger.setAttribute("aria-expanded", "true");
      trigger.setAttribute("aria-describedby", panel.id);
      panel.hidden = false;
      position();
      observer.observe(document.body, { childList: true, subtree: true });
    };
    const from = (target) =>
      target instanceof Element ? target.closest("button[data-boi-search-tooltip]") : null;
    const scheduleClose = () => {
      clearTimeout(closeTimer);
      if (!pinned) closeTimer = setTimeout(close, 180);
    };
    const mount = (host) => {
      hosts.add(host);
      if (controller) return;
      controller = new AbortController();
      const signal = controller.signal;
      observer = new MutationObserver(() => {
        if (active && !active.isConnected) close();
      });
      document.addEventListener(
        "pointerover",
        (event) => {
          if (event.pointerType === "touch") return;
          if (inside(event.target)) clearTimeout(closeTimer);
          const trigger = from(event.target);
          if (trigger) open(trigger);
        },
        { signal },
      );
      document.addEventListener(
        "pointerout",
        (event) => {
          if (inside(event.target) && !inside(event.relatedTarget)) scheduleClose();
        },
        { signal },
      );
      document.addEventListener(
        "focusin",
        (event) => {
          const trigger = from(event.target);
          if (trigger) open(trigger);
          else if (!inside(event.target)) close();
        },
        { signal },
      );
      document.addEventListener(
        "focusout",
        (event) => {
          if (inside(event.target) && !inside(event.relatedTarget)) scheduleClose();
        },
        { signal },
      );
      document.addEventListener(
        "click",
        (event) => {
          const trigger = from(event.target);
          if (!trigger) return;
          event.preventDefault();
          if (active === trigger && pinned) return close();
          open(trigger);
          pinned = active === trigger;
        },
        { signal },
      );
      document.addEventListener(
        "pointerdown",
        (event) => {
          if (active && !inside(event.target)) close();
        },
        { signal },
      );
      document.addEventListener(
        "keydown",
        (event) => {
          if (event.key === "Escape" && active) {
            event.preventDefault();
            event.stopPropagation();
            close();
          }
        },
        { capture: true, signal },
      );
      document.addEventListener("scroll", queuePosition, { passive: true, capture: true, signal });
      window.addEventListener("resize", queuePosition, { passive: true, signal });
      window.visualViewport?.addEventListener("resize", queuePosition, { passive: true, signal });
      window.visualViewport?.addEventListener("scroll", queuePosition, { passive: true, signal });
    };
    return {
      mount,
      owns: (host, target) => owner === host && inside(target),
      closeFor: (host) => {
        if (owner === host) close();
      },
      release: (host) => {
        if (owner === host) close();
        hosts.delete(host);
        tooltipCopyCache.delete(host);
        if (hosts.size) return;
        controller?.abort();
        controller = null;
        observer?.disconnect();
        panel?.remove();
        panel = null;
      },
    };
  })();
  const productBadges = (host, product) => {
    const badges = element("span", { class: "boi-search-card__icon-badges" });
    const tags = new Set(product.tags || []);
    const inventory = product.selectedVariant?.inventoryQuantity;
    const add = (kind, icon, key, quantity) => {
      const copy = tooltipCopy(host, key);
      const title = quantity
        ? `${localeNumber(quantity, host.dataset.locale)} ${copy.title}`
        : copy.title;
      const badge = element(title ? "button" : "span", {
        class: `boi-search-card__icon-badge is-${kind} is-has-image`,
        ...(title
          ? {
              type: "button",
              "aria-label": [title, copy.body].filter(Boolean).join(". "),
              "aria-expanded": "false",
              dataset: { boiSearchTooltip: "" },
            }
          : { "aria-hidden": "true" }),
      });
      badge.append(element("img", { src: icon, alt: "", width: 35, height: 35, loading: "lazy" }));
      if (title) {
        badgeContent.set(badge, { ...copy, title, host });
        searchTooltips.mount(host);
      }
      badges.append(badge);
    };
    if (tags.has("premiato"))
      add("award", host.dataset.awardIcon || runtimeAwardIcon, "tooltip_award");
    if (tags.has("marketplace"))
      add(
        "marketplace",
        "https://cdn.shopify.com/s/files/1/0538/2268/5356/files/marketplace.svg?v=1776413337",
        "tooltip_marketplace",
      );
    if (typeof inventory === "number" && Number.isFinite(inventory)) {
      if (tags.has("sped-2-5") && inventory > 0) {
        const quantity = inventory > 100 ? inventory - 100 : 0;
        add(
          "shipping is-yellow",
          "https://cdn.shopify.com/s/files/1/0538/2268/5356/files/sped-2-5-availability.svg?v=1753964190",
          quantity === 1
            ? "tooltip_shipping_one"
            : quantity > 1
              ? "tooltip_shipping_many"
              : "tooltip_shipping",
          quantity,
        );
      } else if (!tags.has("sped-2-5") && !tags.has("sped-4-10") && inventory !== 0) {
        add(
          "shipping is-green",
          "https://cdn.shopify.com/s/files/1/0538/2268/5356/files/immediate-availability.svg?v=1753958561",
          "tooltip_immediate",
        );
      }
    }
    return badges.childNodes.length ? badges : null;
  };

  class BottleAutocomplete extends HTMLElement {
    connectedCallback() {
      if (this.initialized || !this.dataset.apiBase) return;
      if (
        ((this.initialized = !0),
        contextFor(this)
          .then((context) => learning(this, context))
          .catch(() => {}),
        (this.dataset.runtimeState = "connecting"),
        (this.t = translations(this)),
        (this.instanceId = uuid()),
        (this.form = this.resolveForm()),
        (this.input = this.resolveInput()),
        !this.form || !this.input)
      ) {
        ((this.dataset.runtimeState = "waiting-for-form"),
          (this.initialized = !1),
          (this.retryCount = (this.retryCount || 0) + 1),
          this.retryCount <= 20 &&
            (this.retryTimer = window.setTimeout(() => this.connectedCallback(), 250)));
        return;
      }
      ((this.panel = element("div", {
        class: "boi-autocomplete",
        role: "listbox",
        hidden: !0,
        id: `boi-list-${this.instanceId}`,
        "aria-label": this.t.suggestions,
      })),
        document.body.append(this.panel),
        (this.lifecycleController = new AbortController()));
      const lifecycleSignal = this.lifecycleController.signal;
      (this.input.setAttribute("role", "combobox"),
        this.input.setAttribute("aria-autocomplete", "list"),
        this.input.setAttribute("aria-controls", this.panel.id),
        this.input.setAttribute("aria-expanded", "false"),
        this.input.setAttribute("autocomplete", "off"),
        (this.input.dataset.boiSearchInput = "true"),
        this.input.classList.add("snize-exclude-input"),
        (this.form.dataset.boiSearchOwned = "true"),
        (this.form.dataset.boiSearchForm = "true"),
        (this.searchAction = searchUrl(this.dataset.rootUrl, "").split("?")[0]),
        this.form.setAttribute("action", this.searchAction),
        (this.formObserver = new MutationObserver(() => {
          this.form.getAttribute("action") !== this.searchAction &&
            this.form.setAttribute("action", this.searchAction);
        })),
        this.formObserver.observe(this.form, { attributes: !0, attributeFilter: ["action"] }),
        (this.inputObserver = new MutationObserver(() => {
          (this.input.classList.contains("snize-exclude-input") ||
            this.input.classList.add("snize-exclude-input"),
            this.input.getAttribute("role") !== "combobox" &&
              this.input.setAttribute("role", "combobox"),
            this.input.getAttribute("aria-controls") !== this.panel.id &&
              this.input.setAttribute("aria-controls", this.panel.id));
          const expanded = String(!this.panel.hidden);
          this.input.getAttribute("aria-expanded") !== expanded &&
            this.input.setAttribute("aria-expanded", expanded);
        })),
        this.inputObserver.observe(this.input, {
          attributes: !0,
          attributeFilter: ["class", "role", "aria-controls", "aria-expanded"],
        }),
        (this.options = []),
        (this.selected = -1),
        (this.sequence = 0),
        (this.controller = null),
        (this.search = debounce(() => this.performSearch(), 150)),
        this.input.addEventListener(
          "input",
          (event) => {
            (event.stopImmediatePropagation(), this.search());
          },
          { capture: !0, signal: lifecycleSignal },
        ));
      for (const eventName of ["keyup", "change"])
        this.input.addEventListener(eventName, (event) => event.stopImmediatePropagation(), {
          capture: !0,
          signal: lifecycleSignal,
        });
      (this.input.addEventListener("keydown", (event) => this.onKeydown(event), {
        capture: !0,
        signal: lifecycleSignal,
      }),
        this.form.addEventListener(
          "submit",
          (event) => {
            if (this.selected >= 0 && this.options[this.selected]) {
              (event.preventDefault(),
                event.stopImmediatePropagation(),
                this.options[this.selected].click());
              return;
            }
            const query = this.input.value.trim();
            query &&
              (event.preventDefault(), event.stopImmediatePropagation(), this.submitQuery(query));
          },
          { capture: !0, signal: lifecycleSignal },
        ),
        this.form.addEventListener(
          "click",
          (event) => {
            const submitter = event.target.closest?.("button[type='submit'],input[type='submit']");
            if (!submitter || !this.form.contains(submitter)) return;
            const query = this.input.value.trim();
            query &&
              (event.preventDefault(), event.stopImmediatePropagation(), this.submitQuery(query));
          },
          { capture: !0, signal: lifecycleSignal },
        ),
        document.addEventListener(
          "pointerdown",
          (event) => {
            !this.form.contains(event.target) &&
              !this.panel.contains(event.target) &&
              !searchTooltips.owns(this, event.target) &&
              this.close();
          },
          { signal: lifecycleSignal },
        ),
        document.addEventListener(
          "focusin",
          (event) => {
            !this.form.contains(event.target) &&
              !this.panel.contains(event.target) &&
              !searchTooltips.owns(this, event.target) &&
              this.close();
          },
          { signal: lifecycleSignal },
        ),
        window.addEventListener("resize", () => this.placePanel(), {
          passive: !0,
          signal: lifecycleSignal,
        }),
        window.addEventListener("scroll", () => this.placePanel(), {
          passive: !0,
          signal: lifecycleSignal,
        }),
        (this.dataset.runtimeState = "ready"));
    }
    disconnectedCallback() {
      queueMicrotask(() => {
        this.isConnected ||
          (window.clearTimeout(this.retryTimer),
          this.search?.cancel(),
          this.controller?.abort(),
          this.lifecycleController?.abort(),
          searchTooltips.release(this),
          this.inputObserver?.disconnect(),
          this.formObserver?.disconnect(),
          this.panel?.remove(),
          (this.initialized = !1));
      });
    }
    resolveForm() {
      if (this.previousElementSibling?.matches("form")) return this.previousElementSibling;
      if (this.dataset.formSelector) return document.querySelector(this.dataset.formSelector);
      const input =
        this.dataset.inputSelector && document.querySelector(this.dataset.inputSelector);
      return input?.form
        ? input.form
        : this.previousElementSibling?.matches("form")
          ? this.previousElementSibling
          : this.closest("form");
    }
    resolveInput() {
      return this.dataset.inputSelector
        ? (this.previousElementSibling?.matches("form")
            ? this.previousElementSibling.querySelector("input[name='q']")
            : null) || document.querySelector(this.dataset.inputSelector)
        : this.form?.querySelector("input[name='q']");
    }
    submitQuery(query) {
      this.submitting ||
        ((this.submitting = !0),
        this.close(),
        window.location.assign(searchUrl(this.dataset.rootUrl, query)));
    }
    placePanel() {
      if (!this.panel || this.panel.hidden || !this.form) return;
      const rect = this.form.getBoundingClientRect();
      const gap = matchMedia("(max-width: 900px)").matches ? 0 : 14;
      if (
        (this.panel.style.setProperty(
          "--boi-autocomplete-top",
          `${Math.max(0, rect.bottom + gap)}px`,
        ),
        matchMedia("(min-width: 901px)").matches)
      ) {
        const width = Math.min(1120, innerWidth - 32),
          left = Math.min(
            innerWidth - width - 16,
            Math.max(16, rect.left + rect.width / 2 - width / 2),
          );
        (this.panel.style.setProperty("--boi-autocomplete-left", `${left}px`),
          this.panel.style.setProperty("--boi-autocomplete-width", `${width}px`));
      } else
        (this.panel.style.removeProperty("--boi-autocomplete-left"),
          this.panel.style.removeProperty("--boi-autocomplete-width"));
    }
    close({ restoreFocus = !1 } = {}) {
      searchTooltips.closeFor(this);
      (this.controller?.abort(),
        this.search?.cancel(),
        this.panel &&
          ((this.panel.hidden = !0),
          document.documentElement.classList.remove("boi-search-overlay-open"),
          this.input.setAttribute("aria-expanded", "false"),
          this.input.removeAttribute("aria-activedescendant"),
          (this.selected = -1),
          restoreFocus && this.input.focus({ preventScroll: !0 })));
    }
    choose(index) {
      if (!this.options.length) return;
      const next = (index + this.options.length) % this.options.length;
      (this.options.forEach((node, optionIndex) =>
        node.setAttribute("aria-selected", String(optionIndex === next)),
      ),
        (this.selected = next),
        this.input.setAttribute("aria-activedescendant", this.options[next].id),
        this.options[next].scrollIntoView({ block: "nearest" }));
    }
    onKeydown(event) {
      if (event.key === "Escape" && !this.panel.hidden) {
        (event.preventDefault(),
          event.stopImmediatePropagation(),
          this.close({ restoreFocus: !0 }));
        return;
      }
      if (!this.panel.hidden) {
        if (event.key === "ArrowDown")
          (event.preventDefault(),
            event.stopImmediatePropagation(),
            this.choose(this.selected + 1));
        else if (event.key === "ArrowUp")
          (event.preventDefault(),
            event.stopImmediatePropagation(),
            this.choose(this.selected - 1));
        else if (event.key === "Enter" && this.selected >= 0)
          (event.preventDefault(),
            event.stopImmediatePropagation(),
            this.options[this.selected].click());
        else if (event.key === "Enter") {
          const query = this.input.value.trim();
          if (!query) return;
          (event.preventDefault(), event.stopImmediatePropagation(), this.submitQuery(query));
        }
      }
    }
    registerOption(link) {
      return (
        (link.id = `boi-option-${this.instanceId}-${this.options.length}`),
        link.setAttribute("role", "option"),
        link.setAttribute("aria-selected", "false"),
        link.addEventListener("click", () => this.close()),
        this.options.push(link),
        link
      );
    }
    productCard(product, index, data) {
      const wrapper = element("div", { class: "boi-autocomplete__card" });
      const link = this.registerOption(
        element("a", {
          class: "boi-autocomplete__product",
          href: productUrl(this.dataset.rootUrl, product.handle),
        }),
      );
      const media = element("span", { class: "boi-autocomplete__product-media" });
      if (product.imageUrl)
        media.append(
          element("img", {
            src: product.imageUrl,
            alt: "",
            loading: index < 3 ? "eager" : "lazy",
            decoding: "async",
            width: 120,
            height: 190,
          }),
        );
      media.append(
        element(
          "span",
          {
            class: `boi-autocomplete__stock boi-autocomplete__stock--${product.availability}`,
          },
          inventoryLabel(product.availability, this.t),
        ),
      );
      const details = element("span", { class: "boi-autocomplete__product-details" });
      if (product.producer)
        details.append(element("span", { class: "boi-autocomplete__producer" }, product.producer));
      details.append(element("span", { class: "boi-autocomplete__product-title" }, product.title));
      const metadata = cardMetadata(this, product);
      if (metadata) details.append(metadata);
      details.append(premiumPrice(this, data, product, "boi-autocomplete__price"));
      link.append(media, details);
      link.addEventListener("click", () =>
        sendProductClick(this.dataset.apiBase, data, product, index + 1, "autocomplete"),
      );
      wrapper.append(link);
      const badges = productBadges(this, product);
      if (badges) wrapper.append(badges);
      return wrapper;
    }
    render(data, query) {
      searchTooltips.closeFor(this);
      this.panel.replaceChildren();
      this.options = [];
      this.selected = -1;
      const isMobile = matchMedia("(max-width: 900px)").matches;
      const closeButton = element(
        "button",
        { class: "boi-autocomplete__close", type: "button", "aria-label": this.t.close },
        "×",
      );
      closeButton.addEventListener("click", () => {
        this.close();
        this.input.blur();
      });
      this.panel.append(closeButton);
      const productLimit = isMobile ? 4 : 6;
      const products = Array.isArray(data.products) ? data.products.slice(0, productLimit) : [];
      const suggestions = Array.isArray(data.suggestions) ? data.suggestions.slice(0, 7) : [];
      const navigation = Array.isArray(data.navigation) ? data.navigation.slice(0, 7) : [];
      if (!products.length && !suggestions.length && !navigation.length) {
        this.panel.append(
          element("div", { class: "boi-autocomplete__status", role: "status" }, this.t.no_results),
        );
      } else {
        const layout = element("div", { class: "boi-autocomplete__layout" });
        const discovery = element("div", { class: "boi-autocomplete__discovery" });
        const appendDiscovery = () => {
          if (suggestions.length) {
            const group = element("section", { class: "boi-autocomplete__group", role: "group" });
            group.append(element("h2", { class: "boi-autocomplete__heading" }, this.t.suggestions));
            suggestions.forEach((item) => {
              const link = this.registerOption(
                element(
                  "a",
                  {
                    class: "boi-autocomplete__suggestion",
                    href: searchUrl(this.dataset.rootUrl, item.label),
                  },
                  item.label,
                ),
              );
              group.append(link);
            });
            discovery.append(group);
          }
          if (navigation.length) {
            const group = element("section", { class: "boi-autocomplete__group", role: "group" });
            group.append(element("h2", { class: "boi-autocomplete__heading" }, this.t.categories));
            navigation.forEach((item) => {
              const heading = item.resourceType === "page" ? this.t.pages : this.t.collections;
              const link = this.registerOption(
                element("a", {
                  class: "boi-autocomplete__navigation",
                  href: localizedNavigationPath(item, this.dataset.rootUrl),
                  "aria-label": `${item.title}, ${heading}`,
                }),
              );
              link.append(element("span", {}, item.title), element("small", {}, heading));
              group.append(link);
            });
            discovery.append(group);
          }
        };
        if (!isMobile) appendDiscovery();
        const productArea = element("section", {
          class: "boi-autocomplete__products",
          role: "group",
        });
        productArea.append(element("h2", { class: "boi-autocomplete__heading" }, this.t.products));
        const total = Number.isFinite(Number(data.total)) ? Number(data.total) : products.length;
        const viewAll = element(
          "a",
          { class: "boi-autocomplete__view-all", href: searchUrl(this.dataset.rootUrl, query) },
          `${countText(this.t, "view_all_template", total, this.dataset.locale)} →`,
        );
        if (isMobile) productArea.append(this.registerOption(viewAll));
        const grid = element("div", { class: "boi-autocomplete__product-grid" });
        products.forEach((product, index) => grid.append(this.productCard(product, index, data)));
        productArea.append(grid);
        if (!isMobile) productArea.append(this.registerOption(viewAll));
        if (isMobile) {
          appendDiscovery();
          layout.append(productArea, discovery);
        } else {
          layout.append(discovery, productArea);
        }
        this.panel.append(layout);
      }
      this.panel.hidden = false;
      document.documentElement.classList.add("boi-search-overlay-open");
      this.input.setAttribute("aria-controls", this.panel.id);
      this.input.setAttribute("aria-expanded", "true");
      this.placePanel();
    }
    async performSearch() {
      const query = this.input.value.trim(),
        sequence = ++this.sequence;
      if (query.length < 2) {
        this.close();
        return;
      }
      (this.controller?.abort(),
        (this.controller = new AbortController()),
        delete this.boiB2BDiagnostic);
      try {
        const data = await request(
          this,
          `/v1/autocomplete?q=${encodeURIComponent(query)}&locale=${encodeURIComponent(apiLocale(this))}&marketCountry=${encodeURIComponent(this.dataset.country)}&limit=6`,
          this.controller.signal,
        );
        sequence === this.sequence && query === this.input.value.trim() && this.render(data, query);
      } catch (error) {
        error.name !== "AbortError" &&
          sequence === this.sequence &&
          (this.boiB2BDiagnostic || reportB2BFailure(this, "render", error), this.close());
      }
    }
  }
  class BottleResults extends HTMLElement {
    connectedCallback() {
      if (this.initialized || !this.dataset.apiBase) return;
      this.initialized = true;
      void contextFor(this)
        .then((context) => learning(this, context))
        .catch(() => {});
      this.abort = null;
      this.sequence = 0;
      this.t = translations(this);
      const native =
        this.dataset.nativeResultsSelector &&
        document.querySelector(this.dataset.nativeResultsSelector);
      if (native) native.hidden = true;
      const query = this.params().get("q")?.trim() || "";
      const restored = history.state?.boiSearch;
      if (restored?.journeyId && restored.query === query) {
        this.journeyId = restored.journeyId;
        this.nextInteractionKind = "RETRY";
      } else {
        this.journeyId = uuid();
        this.nextInteractionKind = "INTENT";
        history.replaceState(
          { ...(history.state || {}), boiSearch: { journeyId: this.journeyId, query } },
          "",
        );
      }
      this.onPopState = (event) => {
        const restoredJourney = event.state?.boiSearch;
        const currentQuery = this.params().get("q")?.trim() || "";
        this.journeyId =
          restoredJourney?.journeyId && restoredJourney.query === currentQuery
            ? restoredJourney.journeyId
            : uuid();
        this.nextInteractionKind = "RETRY";
        this.load();
      };
      window.addEventListener("popstate", this.onPopState);
      this.onDocumentPointerDown = (event) => {
        if (event.target.closest?.(".boi-search-card .qty-product--collection")) return;
        this.querySelectorAll(".qty-grid.open").forEach((grid) => grid.classList.remove("open"));
        this.querySelectorAll(".qty-product--collection.qty-open").forEach((widget) =>
          widget.classList.remove("qty-open"),
        );
      };
      document.addEventListener("pointerdown", this.onDocumentPointerDown);
      this.filterViewport = window.matchMedia("(max-width: 900px)");
      this.onFilterViewportChange = () => {
        const shell = this.querySelector(".boi-search__shell");
        const facets = shell?.querySelector(".boi-search__facets");
        if (!facets) return;
        if (!this.filterViewport.matches && shell.dataset.filtersOpen === "true")
          this.closeFilters(shell, facets);
        facets.setAttribute(
          "aria-hidden",
          String(this.filterViewport.matches && shell.dataset.filtersOpen !== "true"),
        );
      };
      this.filterViewport.addEventListener("change", this.onFilterViewportChange);
      this.load();
    }
    disconnectedCallback() {
      searchTooltips.release(this);
      document.documentElement.classList.remove("boi-search-filters-open");
      this.abort?.abort();
      window.removeEventListener("popstate", this.onPopState);
      document.removeEventListener("pointerdown", this.onDocumentPointerDown);
      this.filterViewport?.removeEventListener("change", this.onFilterViewportChange);
    }
    params() {
      return new URL(location.href).searchParams;
    }
    apiParams() {
      const source = this.params(),
        params = new URLSearchParams(),
        query = source.get("q")?.trim();
      query && params.set("q", query);
      for (const key of ["page", "sort", ...ALL_FILTER_PARAMS])
        source.getAll(key).forEach((value) => {
          value && params.append(key, value);
        });
      return (
        params.set("locale", apiLocale(this)),
        params.set("marketCountry", this.dataset.country),
        params.set("perPage", this.dataset.perPage || "24"),
        params.set("journeyId", this.journeyId),
        params.set("interactionKind", this.nextInteractionKind || "RETRY"),
        params
      );
    }
    navigate(mutate, { resetPage = !0, replace = !1 } = {}) {
      const params = this.params(),
        beforeQuery = params.get("q")?.trim() || "",
        beforePage = params.get("page") || "1";
      (mutate(params), resetPage && params.set("page", "1"));
      const afterQuery = params.get("q")?.trim() || "",
        afterPage = params.get("page") || "1";
      afterQuery !== beforeQuery
        ? ((this.journeyId = uuid()), (this.nextInteractionKind = "INTENT"))
        : afterPage !== beforePage
          ? (this.nextInteractionKind = "PAGINATION")
          : (this.nextInteractionKind = "REFINEMENT");
      const url = `${location.pathname}?${params.toString()}`;
      (history[replace ? "replaceState" : "pushState"](
        { ...(history.state || {}), boiSearch: { journeyId: this.journeyId, query: afterQuery } },
        "",
        url,
      ),
        this.load());
    }
    setLoading(query) {
      this.setAttribute("aria-busy", "true");
      const existing = this.querySelector(".boi-search__shell");
      if (existing && query === this.renderedQuery) {
        existing.classList.add("is-loading");
        return;
      }
      document.documentElement.classList.remove("boi-search-filters-open");
      const loading = element("div", { class: "boi-search__loading", role: "status" });
      loading.append(element("span", { class: "boi-search__loading-label" }, this.t.loading));
      const skeletons = element("div", {
        class: "boi-search__skeleton-grid",
        "aria-hidden": "true",
      });
      for (let index = 0; index < 6; index++)
        skeletons.append(element("span", { class: "boi-search__skeleton-card" }));
      loading.append(skeletons);
      this.replaceChildren(loading);
    }
    showError() {
      document.documentElement.classList.remove("boi-search-filters-open");
      const box = element("div", { class: "boi-search__error", role: "alert" });
      box.append(
        element("p", {}, this.dataset.b2b === "true" ? this.t.b2b_unavailable : this.t.error),
      );
      const retry = element("button", { type: "button", class: "boi-search__retry" }, this.t.retry);
      (retry.addEventListener("click", () => {
        ((this.nextInteractionKind = "RETRY"), this.load());
      }),
        box.append(retry),
        this.replaceChildren(box));
      const native =
        this.dataset.nativeResultsSelector &&
        document.querySelector(this.dataset.nativeResultsSelector);
      // Retail can fall back to Shopify; B2B must not expose unverified Retail output.
      if (native) native.hidden = this.dataset.b2b === "true";
      emitB2BFailure(this);
    }
    async load() {
      (this.abort?.abort(), (this.abort = new AbortController()));
      const e = ++this.sequence,
        i = this.apiParams(),
        r = i.get("q");
      if (!r) {
        this.replaceChildren();
        return;
      }
      this.setLoading(r);
      delete this.boiB2BDiagnostic;
      this.boiB2BFreshBootstrap = false;
      try {
        let t = await request(this, `/v1/search?${i.toString()}`, this.abort.signal);
        if (e !== this.sequence) return;
        if (t.facetStatus === "unavailable") {
          const n = new URLSearchParams(i);
          n.set("interactionKind", "RETRY");
          try {
            t = await request(this, `/v1/search?${n.toString()}`, this.abort.signal);
          } catch (o) {
            if (o.name === "AbortError") throw o;
          }
        }
        if (e !== this.sequence) return;
        const a = safePath(t.redirect?.path);
        if (a && a !== location.pathname) {
          window.location.assign(new URL(a, location.origin).href);
          return;
        }
        (this.render(t), (this.renderedQuery = r), t.total || this.renderAlternatives(r, e));
        const s =
          this.dataset.nativeResultsSelector &&
          document.querySelector(this.dataset.nativeResultsSelector);
        (s && (s.hidden = !0), (this.nextInteractionKind = "RETRY"));
      } catch (t) {
        if (t.name !== "AbortError" && e === this.sequence) {
          if (!this.boiB2BDiagnostic) reportB2BFailure(this, "render", t);
          this.showError();
        }
      } finally {
        e === this.sequence && this.removeAttribute("aria-busy");
      }
    }
    async renderAlternatives(query, sequence) {
      let data;
      try {
        data = await request(
          this,
          `/v1/autocomplete?q=${encodeURIComponent(query)}&locale=${encodeURIComponent(apiLocale(this))}&marketCountry=${encodeURIComponent(this.dataset.country)}&limit=4`,
          this.abort.signal,
        );
      } catch {
        return;
      }
      if (sequence !== this.sequence || this.abort.signal.aborted) return;
      const labels = (data.suggestions || []).map((item) => item.label).slice(0, 4);
      if (!labels.length) return;
      const box = element("div", { class: "boi-search__dym" });
      (box.append(element("span", { class: "boi-search__dym-label" }, this.t.did_you_mean)),
        labels.forEach((label, index) => {
          index && box.append(element("span", { class: "boi-search__dym-sep" }, "\xB7"));
          const link = element("a", { href: searchUrl(this.dataset.rootUrl, label) }, label);
          (link.addEventListener("click", (event) => {
            (event.preventDefault(), this.navigate((params) => params.set("q", label)));
          }),
            box.append(link));
        }),
        this.querySelector(".boi-search__toolbar")?.after(box));
    }
    removeFilter(param, value) {
      this.navigate((params) => {
        const remaining = params
          .getAll(param)
          .filter((item) => facetIdentity(param, item) !== facetIdentity(param, value));
        params.delete(param);
        remaining.forEach((item) => params.append(param, item));
      });
    }
    renderChips(data) {
      const chips = element("div", {
        class: "boi-search__chips",
        role: "group",
        "aria-label": this.t.applied_filters,
      });
      for (const param of API_FILTER_PARAMS) {
        for (const { value } of groupFacetValues(param, [], this.params().getAll(param))) {
          const label = facetValueLabel(param, value, this.t, this.dataset.locale);
          const chip = element("span", { class: "boi-search__chip" });
          chip.append(element("span", {}, label));
          const remove = element("button", {
            type: "button",
            class: "boi-search__chip-remove",
            "aria-label": formatTemplate(this.t.remove_filter, { value: label }),
          });
          remove.append(element("span", { "aria-hidden": "true" }, "×"));
          remove.addEventListener("click", () => this.removeFilter(param, value));
          chip.append(remove);
          chips.append(chip);
        }
      }
      const currency = this.priceContext?.currency || priceContext(this, data).currency;
      for (const [minKey, maxKey, label] of [
        ["priceMin", "priceMax", this.t.facet_price],
        ["alcoholMin", "alcoholMax", this.t.facet_alcohol],
      ]) {
        const min = this.params().get(minKey);
        const max = this.params().get(maxKey);
        if (min === null && max === null) continue;
        const format = (value) =>
          minKey === "priceMin"
            ? money(Number(value), currency, this.dataset.locale)
            : `${localeNumber(Number(value), this.dataset.locale, 2)}%`;
        const bounds =
          min !== null && max !== null
            ? `${format(min)} – ${format(max)}`
            : formatTemplate(min !== null ? this.t.range_from : this.t.range_to, {
                value: format(min ?? max),
              });
        const text = `${label}: ${bounds}`;
        const chip = element("span", { class: "boi-search__chip" });
        chip.append(element("span", {}, text));
        const remove = element("button", {
          type: "button",
          class: "boi-search__chip-remove",
          "aria-label": formatTemplate(this.t.remove_filter, { value: text }),
        });
        remove.append(element("span", { "aria-hidden": "true" }, "×"));
        remove.addEventListener("click", () =>
          this.navigate((params) => {
            params.delete(minKey);
            params.delete(maxKey);
            if (minKey === "priceMin") params.delete("priceCurrency");
          }),
        );
        chip.append(remove);
        chips.append(chip);
      }
      if (!chips.childNodes.length) return null;
      const clear = element(
        "button",
        { type: "button", class: "boi-search__clear" },
        this.t.clear_filters,
      );
      clear.addEventListener("click", () =>
        this.navigate((params) =>
          [...ALL_FILTER_PARAMS, "priceCurrency"].forEach((key) => params.delete(key)),
        ),
      );
      chips.append(clear);
      return chips;
    }
    renderRangeFacet(facet, kind) {
      const isPrice = kind === "price";
      const minKey = isPrice ? "priceMin" : "alcoholMin";
      const maxKey = isPrice ? "priceMax" : "alcoholMax";
      const context = this.priceContext;
      const decimals = isPrice ? context.decimals : 2;
      const details = this.facetSection(
        facet,
        this.facetDefaultOpen?.has(facet.key) || false,
        Number(this.params().has(minKey) || this.params().has(maxKey)),
      );
      if (isPrice && !context.valid) {
        details.append(
          element(
            "p",
            { class: "boi-search__facet-empty", role: "status" },
            formatTemplate(this.t.price_currency_unavailable, {
              currency: context.currency || "—",
              actual: context.actual,
            }),
          ),
        );
        return details;
      }
      const form = element("form", { class: "boi-search__range-form", noValidate: true });
      const field = (bound, key) => {
        const input = element("input", {
          type: "text",
          inputMode: decimals ? "decimal" : "numeric",
          placeholder: this.t[`range_${bound}_label`],
          "aria-label": formatTemplate(this.t[`${isPrice ? "price" : "alcohol"}_${bound}_aria`], {
            currency: context.currency,
          }),
          dataset: { focusKey: bound },
          autocomplete: "off",
        });
        const value = this.params().get(key);
        input.value =
          value === null ? "" : localeNumber(Number(value), this.dataset.locale, decimals);
        return input;
      };
      const min = field("min", minKey);
      const max = field("max", maxKey);
      const apply = element(
        "button",
        { type: "submit", class: "boi-search__range-apply", dataset: { focusKey: "range-apply" } },
        this.t.apply,
      );
      const error = element("p", { class: "boi-search__range-error", role: "alert", hidden: true });
      error.id = `boi-range-error-${kind}`;
      [min, max].forEach((input) => input.setAttribute("aria-describedby", error.id));
      const validate = () => {
        const lower = parseRangeNumber(
          min.value,
          this.dataset.locale,
          decimals,
          isPrice ? Infinity : 100,
        );
        const upper = parseRangeNumber(
          max.value,
          this.dataset.locale,
          decimals,
          isPrice ? Infinity : 100,
        );
        const reversed =
          lower.valid &&
          upper.valid &&
          lower.value !== null &&
          upper.value !== null &&
          lower.value > upper.value;
        const message =
          !lower.valid || !upper.valid
            ? this.t.range_number_invalid
            : reversed
              ? this.t.range_invalid
              : "";
        min.setAttribute("aria-invalid", String(!lower.valid));
        max.setAttribute("aria-invalid", String(!upper.valid || reversed));
        error.textContent = message;
        error.hidden = !message;
        return { lower, upper, valid: !message };
      };
      [min, max].forEach((input) =>
        input.addEventListener("input", () => {
          if (!error.hidden) validate();
        }),
      );
      form.append(
        element("span", { class: "boi-search__range-unit" }, isPrice ? context.unit : "%"),
        min,
        max,
        apply,
        error,
      );
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        const { lower, upper, valid } = validate();
        if (!valid) {
          (!lower.valid ? min : max).focus();
          return;
        }
        this.navigate((params) => {
          params.delete(minKey);
          params.delete(maxKey);
          if (lower.value !== null) params.set(minKey, String(lower.value));
          if (upper.value !== null) params.set(maxKey, String(upper.value));
          if (isPrice) {
            params.delete("priceCurrency");
            if (lower.value !== null || upper.value !== null)
              params.set("priceCurrency", context.currency);
          }
        });
      });
      details.append(form);
      return details;
    }
    renderFacet(facet, defaultOpen = false) {
      const param = FACET_PARAMS[facet.key];
      if (!param) return null;
      const selectedValues = this.params().getAll(param);
      // Conditional facets omit alternatives after a selection. Retain previously
      // observed choices for OR, but never reuse or invent their old counts.
      const known = selectedValues.length
        ? [...(this.facetAliases.get(param)?.values() || [])].flatMap((values) => [...values])
        : [];
      groupFacetValues(param, facet.values);
      const returned = new Set(
        (facet.values || []).map(({ value }) => facetIdentity(param, value)),
      );
      const groups = groupFacetValues(param, facet.values, [...selectedValues, ...known]);
      groups.forEach((group) => {
        group.countUnknown =
          !returned.has(group.key) && !group.values.some((value) => selectedValues.includes(value));
      });
      if (!groups.length) return null;
      const selected = (group) => group.values.some((value) => selectedValues.includes(value));
      const label = (group) => facetValueLabel(param, group.value, this.t, this.dataset.locale);
      const collator = new Intl.Collator(this.dataset.locale, {
        numeric: true,
        sensitivity: "base",
      });
      groups.sort((a, b) => {
        const active = Number(selected(b)) - Number(selected(a));
        if (active) return active;
        if (param === "vintage" && /^\d{4}$/u.test(a.value) && /^\d{4}$/u.test(b.value))
          return Number(b.value) - Number(a.value);
        if (param === "format") {
          const volume = volumeMl(a.value) - volumeMl(b.value);
          if (!Number.isNaN(volume) && volume !== 0) return volume;
        }
        if (param === "availability") {
          const order = ["in_stock", "backorder", "out_of_stock"];
          return order.indexOf(a.value) - order.indexOf(b.value);
        }
        return collator.compare(label(a), label(b));
      });
      const selectedCount = groups.filter(selected).length;
      const details = this.facetSection(facet, defaultOpen, selectedCount);
      const state = this.facetUi.get(facet.key) || {};
      const list = element("div", {
        class: "boi-search__facet-values",
        id: `boi-values-${uuid()}`,
      });
      const rows = [];
      const collisions = new Map();
      groups.forEach((group) =>
        collisions.set(label(group), (collisions.get(label(group)) || 0) + 1),
      );
      for (const group of groups) {
        const id = `boi-facet-${uuid()}`;
        const labelText = label(group);
        const row = element("label", { class: "boi-search__facet-value", htmlFor: id });
        const input = element("input", {
          type: "checkbox",
          id,
          value: group.value,
          checked: selected(group),
          disabled: group.count === 0 && !selected(group) && !group.countUnknown,
          dataset: { focusKey: `value:${group.key}` },
        });
        input.indeterminate =
          selected(group) && !!group.aliases && !selectedValues.includes(group.value);
        input.addEventListener("change", () => {
          const remaining = this.params()
            .getAll(param)
            .filter((value) => facetIdentity(param, value) !== group.key);
          const aliases = group.aliases
            ? [group.value]
            : this.facetAliases.get(param)?.get(group.key) || group.values;
          const values = [...new Set([...remaining, ...(input.checked ? aliases : [])])];
          if (values.length > (param === "availability" ? 3 : 20)) {
            input.checked = !input.checked;
            input.setCustomValidity(this.t.selection_limit);
            input.reportValidity();
            return;
          }
          input.setCustomValidity("");
          this.navigate((params) => {
            params.delete(param);
            values.forEach((value) => params.append(param, value));
          });
        });
        const name = element("span", {}, labelText);
        // Preserve distinct array values even when presentation labels coincide.
        if (collisions.get(labelText) > 1)
          name.append(element("small", { class: "boi-search__facet-raw" }, ` (${group.value})`));
        row.append(
          input,
          name,
          element(
            "small",
            {
              "aria-label": group.countUnknown ? this.t.facet_count_unavailable : undefined,
              title: group.countUnknown ? this.t.facet_count_unavailable : undefined,
            },
            this.facetCountsUnavailable || facet.countsUnavailable || group.countUnknown
              ? "—"
              : localeNumber(group.count, this.dataset.locale),
          ),
        );
        list.append(row);
        rows.push({ row, input, text: facetSearchText(`${labelText} ${group.values.join(" ")}`) });
      }
      let expanded = state.expanded || false;
      const empty = element(
        "p",
        { class: "boi-search__facet-empty", role: "status" },
        this.t.facet_no_matches,
      );
      const toggle = element("button", {
        type: "button",
        class: "boi-search__facet-toggle",
        "aria-controls": list.id,
        dataset: { focusKey: "more" },
      });
      let search;
      const update = () => {
        const query = facetSearchText(search?.value || "");
        let visible = 0;
        rows.forEach(({ row, input, text }, index) => {
          row.hidden = query
            ? !text.includes(query)
            : !expanded && index >= FACET_INITIAL_VALUES && !input.checked;
          if (!row.hidden) visible++;
        });
        empty.hidden = visible > 0;
        toggle.hidden = Boolean(query) || rows.length <= FACET_INITIAL_VALUES;
        toggle.textContent = expanded ? this.t.show_less : this.t.facet_show_all;
        toggle.setAttribute("aria-expanded", String(expanded));
      };
      if (groups.length > FACET_INITIAL_VALUES) {
        const text = formatTemplate(this.t.search_facet, { facet: facetLabel(facet, this.t) });
        search = element("input", {
          type: "search",
          class: "boi-search__facet-search",
          placeholder: text,
          "aria-label": text,
          "aria-controls": list.id,
          dataset: { focusKey: "search" },
        });
        search.value = state.search || "";
        search.addEventListener("input", () => {
          this.facetUi.set(facet.key, { ...this.facetUi.get(facet.key), search: search.value });
          update();
        });
        details.append(search);
      }
      toggle.addEventListener("click", () => {
        expanded = !expanded;
        this.facetUi.set(facet.key, { ...this.facetUi.get(facet.key), expanded });
        update();
      });
      details.append(list, empty, toggle);
      update();
      return details;
    }
    activeFilterCount() {
      const params = this.params();
      const values = API_FILTER_PARAMS.reduce(
        (count, key) => count + groupFacetValues(key, [], params.getAll(key)).length,
        0,
      );
      const ranges = [
        ["priceMin", "priceMax"],
        ["alcoholMin", "alcoholMax"],
      ].filter(([min, max]) => params.has(min) || params.has(max)).length;
      return values + ranges;
    }
    openFilters(shell, facets, button) {
      if (shell.dataset.filtersOpen !== "true") this.filterScrollPosition = window.scrollY;
      shell.dataset.filtersOpen = "true";
      facets.setAttribute("aria-hidden", "false");
      facets.setAttribute("role", "dialog");
      facets.setAttribute("aria-modal", "true");
      shell.querySelector(".boi-search__main").inert = true;
      button.setAttribute("aria-expanded", "true");
      document.documentElement.classList.add("boi-search-filters-open");
      this.filterTrigger = button;
      facets.querySelector("button")?.focus();
    }
    closeFilters(shell, facets) {
      shell.dataset.filtersOpen = "false";
      facets.setAttribute("aria-hidden", "true");
      facets.removeAttribute("role");
      facets.removeAttribute("aria-modal");
      shell.querySelector(".boi-search__main").inert = false;
      this.filterTrigger?.setAttribute("aria-expanded", "false");
      document.documentElement.classList.remove("boi-search-filters-open");
      this.filterTrigger?.focus({ preventScroll: true });
      if (Number.isFinite(this.filterScrollPosition))
        window.scrollTo({ top: this.filterScrollPosition, behavior: "auto" });
    }
    trapFilterFocus(event, shell, facets) {
      if (shell.dataset.filtersOpen !== "true") return;
      if (event.key === "Escape") {
        (event.preventDefault(), this.closeFilters(shell, facets));
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = [...facets.querySelectorAll("button,input,summary,[href],select")].filter(
        (node) => !node.disabled && node.offsetParent !== null,
      );
      if (!focusable.length) return;
      const first = focusable[0],
        last = focusable[focusable.length - 1];
      event.shiftKey && document.activeElement === first
        ? (event.preventDefault(), last.focus())
        : !event.shiftKey &&
          document.activeElement === last &&
          (event.preventDefault(), first.focus());
    }
    productCard(product, index, data) {
      const b2b = data.pricingContext?.audience === "b2b",
        productId = numericShopifyId(product.productId) || String(index + 1),
        item = element("li", {
          class: `boi-search-card productBox grid__item item-row ${product.availability === "out_of_stock" ? "sold-out" : ""} ${!b2b && discountPercent(product) ? "on-sale" : ""} prod-grid-view`,
          id: `product-${productId}`,
        }),
        card = element("div", {
          class: "boi-search-card__inner products product-hover-11 custom-collection-card",
        }),
        link = productUrl(this.dataset.rootUrl, product.handle),
        form = element("form", {
          class: "boi-search-card__form custom-card-form-wrap",
          id: `cart-form-${productId}`,
          action: `${rootPath(this.dataset.rootUrl)}cart/add`,
          method: "post",
        }),
        container = element("div", {
          class: "boi-search-card__layout product-container custom-card-horizontal",
        }),
        left = element("div", { class: "boi-search-card__left custom-card-left" }),
        iconBadges = productBadges(this, product);
      const inventory = product.selectedVariant?.inventoryQuantity;
      if (iconBadges) left.append(iconBadges);
      const media = element("a", {
        class: "boi-search-card__media custom-card-image-link",
        href: link,
        "aria-label": product.title,
      });
      (product.imageUrl &&
        media.append(
          element("img", {
            class: "featured-image",
            src: product.imageUrl,
            alt: "",
            loading: index < 3 ? "eager" : "lazy",
            decoding: "async",
            width: 130,
            height: 230,
          }),
        ),
        left.append(media));
      const details = element("div", { class: "boi-search-card__details custom-card-right" });
      product.producer &&
        details.append(
          element("span", { class: "boi-search-card__producer product-vendor" }, product.producer),
        );
      const titleHeader = element("div", { class: "custom-card-header-row" }),
        titleLink = element("a", { class: "custom-card-title-link", href: link });
      (titleLink.append(
        element(
          "h2",
          { class: "boi-search-card__title custom-card-title grid-link__title" },
          product.title,
        ),
      ),
        titleHeader.append(titleLink),
        details.append(titleHeader));
      const metadata = cardMetadata(this, product);
      if (metadata) details.append(metadata);
      details.append(premiumPrice(this, data, product, "boi-search-card__price"));
      for (const badge of product.badges || [])
        ["Backorder", "Out of stock"].includes(badge) ||
          details.append(element("span", { class: "boi-search-card__badge" }, badge));
      let cta;
      const knownDeniedInventoryUnavailable =
        product.selectedVariant?.inventoryPolicy === "DENY" &&
        typeof inventory == "number" &&
        inventory <= 0;
      if (
        product.availability !== "out_of_stock" &&
        product.variantId &&
        product.selectedVariant?.availableForSale === !0 &&
        !knownDeniedInventoryUnavailable
      ) {
        form.append(element("input", { type: "hidden", name: "id", value: product.variantId }));
        const hiddenQuantity = element("input", {
          type: "hidden",
          name: "quantity",
          value: 1,
          class: "card-quantity-hidden",
        });
        form.append(hiddenQuantity);
        const leftActions = element("div", { class: "custom-card-left-actions" }),
          quantityWrap = element("div", { class: "custom-card-left-qty" }),
          widget = element("div", {
            class: "qty-product qty-product--collection",
            dataset: { inventory: typeof inventory == "number" ? String(inventory) : "" },
          }),
          inventoryLimit =
            product.selectedVariant?.inventoryPolicy === "DENY" &&
            typeof inventory == "number" &&
            inventory >= 0
              ? inventory
              : Number.POSITIVE_INFINITY,
          inputWrapper = element("div", { class: "qty-input-wrapper" }),
          quantity = element("input", {
            type: "number",
            class: "qty-input",
            min: 1,
            max: Number.isFinite(inventoryLimit) ? inventoryLimit : void 0,
            value: 1,
            readOnly: !0,
            "aria-label": `${this.t.quantity}: ${product.title}`,
          });
        inputWrapper.append(
          quantity,
          element("div", { class: "qty-toggle", "aria-hidden": "true" }, "\u25BC"),
        );
        const quantityGrid = element("div", { class: "qty-grid" }),
          closeQuantity = () => {
            (quantityGrid.classList.remove("open"), widget.classList.remove("qty-open"));
          },
          setQuantity = (value) => {
            const safe = Math.max(1, Math.min(value, inventoryLimit));
            ((quantity.value = String(safe)),
              (hiddenQuantity.value = String(safe)),
              hiddenQuantity.setAttribute("value", String(safe)),
              quantityGrid
                .querySelectorAll(".qty-option")
                .forEach((option) =>
                  option.classList.toggle("selected", Number(option.dataset.value) === safe),
                ));
          };
        for (const value of [1, 2, 3, 4, 6, 12, 18, 24]) {
          const disabled = value > inventoryLimit,
            option = element(
              "button",
              {
                type: "button",
                class: `qty-option${value === 1 ? " selected" : ""}${disabled ? " disabled" : ""}`,
                dataset: { value: String(value) },
                disabled,
              },
              String(value),
            );
          (option.addEventListener("click", () => {
            (setQuantity(value), closeQuantity());
          }),
            quantityGrid.append(option));
        }
        const otherLabels = { it: "Altro", de: "Andere", fr: "Autre", en: "Other" },
          other = element(
            "button",
            { type: "button", class: "qty-altro" },
            otherLabels[this.dataset.locale] || otherLabels.en,
          );
        (other.addEventListener("click", () => {
          (closeQuantity(), (quantity.readOnly = !1), quantity.focus(), quantity.select());
        }),
          quantity.addEventListener("change", () => setQuantity(Number(quantity.value) || 1)),
          inputWrapper.addEventListener("click", () => {
            const opening = !quantityGrid.classList.contains("open");
            (this.querySelectorAll(".qty-grid.open").forEach((grid) =>
              grid.classList.remove("open"),
            ),
              this.querySelectorAll(".qty-product--collection.qty-open").forEach((item2) =>
                item2.classList.remove("qty-open"),
              ),
              quantityGrid.classList.toggle("open", opening),
              widget.classList.toggle("qty-open", opening));
          }),
          quantityGrid.append(other),
          widget.append(inputWrapper, quantityGrid, element("div", { class: "qty-error" })),
          quantityWrap.append(widget),
          leftActions.append(
            quantityWrap,
            element("div", { class: "custom-low-stock-slot-spacer" }),
          ),
          left.append(leftActions));
        const rightActions = element("div", { class: "custom-card-right-actions" }),
          buttonWrap = element("div", { class: "custom-card-right-bottom" });
        ((cta = element(
          "button",
          {
            type: "submit",
            name: "add",
            class: "boi-search-card__cta btn atc-main-btn custom-card-add-btn",
          },
          this.t.add_to_cart,
        )),
          buttonWrap.append(cta),
          rightActions.append(buttonWrap, element("div", { class: "custom-low-stock-slot" })),
          details.append(rightActions));
      } else
        product.availability === "out_of_stock"
          ? details.append(
              element(
                "div",
                { class: "boi-search-card__sold-out custom-card-sold-out-wrap" },
                this.t.sold_out,
              ),
            )
          : ((cta = element(
              "a",
              { class: "boi-search-card__cta custom-card-add-btn", href: link },
              this.t.view_product,
            )),
            details.append(cta));
      cta?.tagName === "BUTTON" &&
        form.addEventListener("submit", async (event) => {
          event.preventDefault();
          const quantity = Number(form.querySelector(".qty-input")?.value || 1),
            hiddenInput = form.querySelector(".card-quantity-hidden");
          (hiddenInput &&
            ((hiddenInput.value = String(quantity)),
            hiddenInput.setAttribute("value", String(quantity))),
            (cta.disabled = !0));
          let added = !1;
          try {
            const addResponse = await fetch(`${rootPath(this.dataset.rootUrl)}cart/add.js`, {
              method: "POST",
              credentials: "same-origin",
              headers: { "content-type": "application/json", accept: "application/json" },
              body: JSON.stringify({ items: [{ id: product.variantId, quantity }] }),
            });
            if (!addResponse.ok) throw new Error(`Cart add ${addResponse.status}`);
            ((added = !0), directQuickAddVariants.add(String(product.variantId)));
            const cartResponse = await fetch(`${rootPath(this.dataset.rootUrl)}cart.js`, {
              credentials: "same-origin",
              headers: { accept: "application/json" },
            });
            if (!cartResponse.ok) throw new Error(`Cart ${cartResponse.status}`);
            const cart = await cartResponse.json();
            (document.dispatchEvent(
              new CustomEvent("bottle-search:cart-added", { detail: { cart } }),
            ),
              cart.token &&
                data.analyticsToken &&
                data.searchId &&
                analyticsAllowed() &&
                fetch(`${this.dataset.apiBase.replace(/\/$/, "")}/v1/analytics/cart`, {
                  method: "POST",
                  credentials: "omit",
                  keepalive: !0,
                  headers: {
                    "content-type": "application/json",
                    "x-search-session": sessionId,
                    ...consentHeaders(),
                  },
                  body: JSON.stringify({
                    eventId: uuid(),
                    searchId: data.searchId,
                    analyticsToken: data.analyticsToken,
                    cartToken: analyticsCartToken(cart.token),
                    productId: product.productId,
                    variantId: String(product.variantId),
                    quantity,
                    attributionSource: "DIRECT_QUICK_ADD",
                  }),
                }).catch(() => {}));
          } catch {
            added || form.submit();
          } finally {
            cta.disabled = !1;
          }
        });
      for (const clickable of [media, titleLink, cta].filter(Boolean))
        clickable.addEventListener(
          "click",
          () =>
            clickable.tagName === "A" &&
            sendProductClick(this.dataset.apiBase, data, product, index + 1, "results"),
        );
      return (
        container.append(left, details),
        form.append(container),
        card.append(form),
        item.append(card),
        item
      );
    }
    renderPagination(data) {
      if (data.totalPages <= 1) return null;
      const nav = element("nav", {
          class: "boi-search__pagination",
          "aria-label": this.t.pagination_aria,
        }),
        addPage = (page, label, className) => {
          const button = element(
            "button",
            {
              type: "button",
              class: className,
              disabled: page < 1 || page > data.totalPages,
              "aria-current": page === data.page ? "page" : null,
              "aria-label": label,
            },
            className ? label : String(page),
          );
          (button.addEventListener("click", () => {
            (this.navigate((params) => params.set("page", String(page)), { resetPage: !1 }),
              scrollTo({ top: Math.max(0, this.offsetTop - 120), behavior: "smooth" }));
          }),
            nav.append(button));
        };
      addPage(data.page - 1, `\u2190 ${this.t.previous_page}`, "boi-search__pagination-edge");
      for (
        let page = Math.max(1, data.page - 2);
        page <= Math.min(data.totalPages, data.page + 2);
        page++
      )
        addPage(page, `${this.t.pagination_aria} ${page}`, "");
      return (
        addPage(data.page + 1, `${this.t.next_page} \u2192`, "boi-search__pagination-edge"),
        nav
      );
    }
    render(data) {
      const previousShell = this.querySelector(".boi-search__shell");
      const keepDrawer =
        previousShell?.dataset.filtersOpen === "true" && this.renderedQuery === data.query;
      const previousAside = previousShell?.querySelector(".boi-search__facets");
      const scrollTop = previousAside?.querySelector(".boi-search__facets-body")?.scrollTop || 0;
      const active = document.activeElement;
      const focusFacet = active?.closest?.("[data-facet-key]")?.dataset.facetKey;
      const focusKey = active?.dataset.focusKey;
      this.priceContext = priceContext(this, data);
      const preparedFacets = this.prepareFacets(data);
      this.facetCountsUnavailable = data.facetStatus === "unavailable";
      document.documentElement.classList.remove("boi-search-filters-open");
      const shell = element("div", {
        class: "boi-search__shell",
        dataset: { filtersOpen: "false" },
      });
      const facets = element("aside", {
        class: "boi-search__facets",
        "aria-label": this.t.facets_aria,
        "aria-hidden": "false",
      });
      const facetHeader = element("div", { class: "boi-search__facets-header" });
      const activeCount = this.activeFilterCount();
      const facetTitle = element("h2", {}, this.t.filter_results);
      if (activeCount)
        facetTitle.append(
          element(
            "span",
            { class: "boi-search__facets-count" },
            localeNumber(activeCount, this.dataset.locale),
          ),
        );
      facetHeader.append(facetTitle);
      const close = element("button", {
        type: "button",
        class: "boi-search__facets-close",
        "aria-label": this.t.close,
      });
      close.append(element("span", { "aria-hidden": "true" }, "×"));
      close.addEventListener("click", () => this.closeFilters(shell, facets));
      facetHeader.append(close);
      facets.append(facetHeader);
      const facetBody = element("div", { class: "boi-search__facets-body" });
      facets.append(facetBody);
      facetBody.append(element("p", { class: "boi-search__filters-hint" }, this.t.filters_live));
      if (data.facetStatus === "unavailable")
        facetBody.append(
          element(
            "p",
            { role: "status", class: "boi-search__facet-empty" },
            this.t.facets_unavailable,
          ),
        );
      const drawerChips = this.renderChips(data);
      if (drawerChips) {
        drawerChips.classList.add("boi-search__drawer-chips");
        facetBody.append(drawerChips);
      }
      let facetIndex = 0;
      for (const facet of preparedFacets) {
        if (facet.key === "category") continue;
        let rendered = null;
        if (facet.key === "price" && facet.type === "range")
          rendered = this.renderRangeFacet(facet, "price");
        else if (facet.key === "alcohol_by_volume") {
          let alcoholFacet = facet;
          if (facet.type === "values") {
            const values = facet.values
              ?.map((item) => Number(item.value))
              .filter((value) => Number.isFinite(value));
            if (values?.length)
              alcoholFacet = {
                ...facet,
                type: "range",
                range: { min: Math.min(...values), max: Math.max(...values) },
              };
          }
          if (alcoholFacet.type === "range")
            rendered = this.renderRangeFacet(alcoholFacet, "alcohol");
        } else if (facet.type === "values")
          rendered = this.renderFacet(facet, this.facetDefaultOpen?.has(facet.key) || false);
        if (rendered) {
          facetBody.append(rendered);
          facetIndex++;
        }
      }
      const facetFooter = element("div", { class: "boi-search__facets-footer" });
      if (activeCount) {
        const clear = element(
          "button",
          { type: "button", class: "boi-search__facets-clear" },
          this.t.clear_filters,
        );
        clear.addEventListener("click", () =>
          this.navigate((params) =>
            [...ALL_FILTER_PARAMS, "priceCurrency"].forEach((key) => params.delete(key)),
          ),
        );
        facetFooter.append(clear);
      }
      const showResults = element(
        "button",
        { type: "button", class: "boi-search__facets-apply" },
        countText(this.t, "show_results_template", data.total, this.dataset.locale),
      );
      showResults.addEventListener("click", () => this.closeFilters(shell, facets));
      facetFooter.append(showResults);
      facets.append(facetFooter);
      const main = element("section", { class: "boi-search__main" });
      const toolbar = element("div", { class: "boi-search__toolbar" });
      const filterButton = element(
        "button",
        { type: "button", class: "boi-search__mobile-filters", "aria-expanded": "false" },
        activeCount
          ? countText(this.t, "filter_count_template", activeCount, this.dataset.locale)
          : this.t.filters,
      );
      filterButton.addEventListener("click", () => this.openFilters(shell, facets, filterButton));
      const status = element(
        "div",
        { class: "boi-search__status", role: "status", "aria-live": "polite" },
        countText(this.t, "status_template", data.total, this.dataset.locale, {
          query: data.query,
        }),
      );
      const sortWrap = element("label", { class: "boi-search__sort" });
      sortWrap.append(element("span", {}, this.t.sort));
      const sort = element("select", { "aria-label": this.t.sort });
      for (const [value, label] of [
        ["relevance", this.t.sort_relevance],
        ["price_asc", this.t.sort_price_asc],
        ["price_desc", this.t.sort_price_desc],
        ["newest", this.t.sort_newest],
      ])
        sort.append(element("option", { value, selected: data.sort === value }, label));
      sort.addEventListener("change", () =>
        this.navigate((params) => params.set("sort", sort.value)),
      );
      sortWrap.append(sort);
      if (facetIndex) toolbar.append(filterButton);
      toolbar.append(status, sortWrap);
      main.append(toolbar);
      const chips = this.renderChips(data);
      if (chips) main.append(chips);
      const grid = element("ul", { class: "boi-search__grid products-grid-view" });
      data.results.forEach((product, index) => grid.append(this.productCard(product, index, data)));
      main.append(grid);
      if (!data.total)
        main.append(
          element(
            "div",
            { class: "boi-search__empty", role: "status" },
            `${this.t.no_results} “${data.query}”`,
          ),
        );
      const pagination = this.renderPagination(data);
      if (pagination) main.append(pagination);
      shell.addEventListener("keydown", (event) => this.trapFilterFocus(event, shell, facets));
      if (facetIndex) shell.append(facets);
      shell.append(main);
      this.replaceChildren(shell);
      this.onFilterViewportChange?.();
      if (keepDrawer && facetIndex && this.filterViewport?.matches) {
        const origin = this.filterScrollPosition;
        this.openFilters(shell, facets, filterButton);
        this.filterScrollPosition = origin;
        facetBody.scrollTop = scrollTop;
      }
      if (focusFacet && focusKey) {
        const section = [...facets.querySelectorAll("[data-facet-key]")].find(
          (node) => node.dataset.facetKey === focusFacet,
        );
        const target =
          section &&
          [...section.querySelectorAll("[data-focus-key]")].find(
            (node) => node.dataset.focusKey === focusKey,
          );
        if (target?.offsetParent !== null) target?.focus({ preventScroll: true });
      }
    }
    facetSection(facet, defaultOpen, selectedCount) {
      const state = this.facetUi.get(facet.key) || {};
      const details = element("details", {
        class: "boi-search__facet",
        dataset: { facetKey: facet.key },
        open: state.open ?? defaultOpen,
      });
      const summary = element("summary", { dataset: { focusKey: "summary" } });
      summary.append(element("span", {}, facetLabel(facet, this.t)));
      if (selectedCount)
        summary.append(
          element(
            "span",
            { class: "boi-search__facets-count" },
            localeNumber(selectedCount, this.dataset.locale),
          ),
        );
      details.append(summary);
      details.addEventListener("toggle", () => {
        this.facetUi.set(facet.key, { ...this.facetUi.get(facet.key), open: details.open });
      });
      return details;
    }
    prepareFacets(data) {
      const scope = JSON.stringify([
        data.query,
        this.dataset.locale,
        this.dataset.country,
        this.dataset.b2b,
      ]);
      if (this.facetScope !== scope) {
        this.facetScope = scope;
        this.facetUi = new Map();
        this.facetAliases = new Map();
        this.facetWineContext = undefined;
        this.facetDefaultOpen = undefined;
      }
      const facets = [...(data.facets || [])];
      for (const facet of facets) {
        const param = FACET_PARAMS[facet.key];
        if (!param) continue;
        if (!this.facetAliases.has(param)) this.facetAliases.set(param, new Map());
        const aliases = this.facetAliases.get(param);
        for (const group of groupFacetValues(param, facet.values, this.params().getAll(param))) {
          const known = aliases.get(group.key) || new Set();
          group.values.forEach((value) => known.add(value));
          aliases.set(group.key, known);
        }
      }
      // Selected values remain removable even when a zero-result response omits them.
      for (const [key, param] of Object.entries(FACET_PARAMS))
        if (this.params().has(param) && !facets.some((facet) => facet.key === key))
          facets.push({
            key,
            label: param,
            type: "values",
            values: [],
            countsUnavailable: data.total > 0,
          });
      for (const [kind, min, max] of [
        ["price", "priceMin", "priceMax"],
        ["alcohol_by_volume", "alcoholMin", "alcoholMax"],
      ])
        if (
          (this.params().has(min) || this.params().has(max)) &&
          !facets.some((facet) => facet.key === kind)
        )
          facets.push({ key: kind, type: "range" });
      // Use returned structured values, rather than guessing from arbitrary query text.
      // Preserve context through zero-result/omitted-facet responses for the same query.
      const wineTypes = facets.find((facet) => facet.key === "wine_type")?.values || [];
      const denominations = facets.find((facet) => facet.key === "denomination")?.values || [];
      if (wineTypes.length || denominations.length || this.facetWineContext === undefined) {
        const isWine = ({ value }) =>
          /^(?:vino\s+)?(?:rosso|bianco|rosato|ros[eé]|spumante|frizzante|passito|dolce|orange)(?:\s|$)/iu.test(
            value.trim(),
          );
        const wineCount = wineTypes.filter(isWine).reduce((sum, item) => sum + item.count, 0);
        const totalCount = wineTypes.reduce((sum, item) => sum + item.count, 0);
        const wineDenomination = denominations.some(({ value }) =>
          /\b(?:DOCG?|IGT)\b/iu.test(value),
        );
        // Real wine results also include gift-box descriptions in wine_type.
        // This affects presentation only: retain every raw value and selection.
        this.facetWineContext = wineTypes.length
          ? wineTypes.every(isWine) ||
            (wineDenomination && totalCount > 0 && wineCount / totalCount >= 0.8)
          : wineDenomination;
      }
      const order = this.facetWineContext ? WINE_FACET_ORDER : FACET_ORDER;
      const rank = (facet) => (order.includes(facet.key) ? order.indexOf(facet.key) : order.length);
      facets.sort((a, b) => rank(a) - rank(b));
      const params = this.params();
      const active = (facet) =>
        facet.key === "price"
          ? params.has("priceMin") || params.has("priceMax")
          : facet.key === "alcohol_by_volume"
            ? params.has("alcoholMin") || params.has("alcoholMax")
            : params.has(FACET_PARAMS[facet.key]);
      const useful = (facet) =>
        facet.type === "range"
          ? facet.range?.max > facet.range?.min
          : FACET_PARAMS[facet.key] &&
            groupFacetValues(FACET_PARAMS[facet.key], facet.values).length > 1;
      const initial = [
        ...facets.filter(active),
        ...facets.filter((facet) => !active(facet) && useful(facet)),
      ];
      // A lone returned option remains discoverable; otherwise auto-open at most two.
      if (!initial.length) {
        const single = facets.find((facet) => FACET_PARAMS[facet.key] && facet.values?.length);
        if (single) initial.push(single);
      }
      if (initial.length)
        this.facetDefaultOpen ??= new Set(initial.slice(0, 2).map((facet) => facet.key));
      return facets;
    }
  }
  (customElements.get("bottle-search-autocomplete") ||
    customElements.define("bottle-search-autocomplete", BottleAutocomplete),
    customElements.get("bottle-search-results") ||
      customElements.define("bottle-search-results", BottleResults));
  const installProductPageAttribution = async () => {
    const apiBase = document.querySelector("bottle-search-results,bottle-search-autocomplete")
      ?.dataset.apiBase;
    if (!apiBase) return;
    let quantities;
    try {
      const response = await fetch(`${rootPath(window.Shopify?.routes?.root || "/")}cart.js`, {
        credentials: "same-origin",
        headers: { accept: "application/json" },
      });
      if (!response.ok) return;
      const cart = await response.json();
      quantities = new Map(
        (cart.items || []).map((item) => [
          String(item.variant_id || item.id),
          Number(item.quantity),
        ]),
      );
    } catch {
      return;
    }
    document.addEventListener("cart:updated", (event) => {
      const cart = event.detail?.cart || event.detail;
      if (!cart?.token || !Array.isArray(cart.items)) return;
      const next = new Map(
          cart.items.map((item) => [String(item.variant_id || item.id), Number(item.quantity)]),
        ),
        records = attributionRecords();
      for (const item of cart.items) {
        const variantId = String(item.variant_id || item.id),
          addedQuantity = Number(item.quantity) - (quantities.get(variantId) || 0);
        if (addedQuantity <= 0 || directQuickAddVariants.delete(variantId)) continue;
        const record = [...records]
          .reverse()
          .find((candidate) => String(candidate.productNumericId) === String(item.product_id));
        if (!record) continue;
        const endpoint = `${apiBase.replace(/\/$/, "")}/v1/analytics/cart`,
          body = JSON.stringify({
            eventId: record.eventId,
            searchId: record.searchId,
            analyticsToken: record.attributionToken,
            attributionToken: record.attributionToken,
            attributionSource: "PRODUCT_PAGE",
            cartToken: analyticsCartToken(cart.token),
            productId: record.productId,
            variantId,
            quantity: addedQuantity,
          });
        sendProductPageCart(endpoint, body)
          .then((accepted) => {
            if (accepted)
              try {
                localStorage.setItem(
                  ATTRIBUTION_STORAGE_KEY,
                  JSON.stringify(
                    attributionRecords().filter(
                      (candidate) => candidate.eventId !== record.eventId,
                    ),
                  ),
                );
              } catch {}
          })
          .catch(() => {});
      }
      quantities = next;
    });
  };
  (document.readyState === "loading"
    ? document.addEventListener(
        "DOMContentLoaded",
        () => {
          installProductPageAttribution();
        },
        { once: !0 },
      )
    : installProductPageAttribution(),
    (document.documentElement.dataset.boiSearchRuntime = "ready"));
})();
